import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Stack,
  Avatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  LinearProgress,
  Snackbar,
  Link,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { portal, portalGradientTextSx } from '../styles/dashboardBrand';
import RefreshIcon from '@mui/icons-material/Refresh';
import TodayIcon from '@mui/icons-material/Today';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import GroupIcon from '@mui/icons-material/Group';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PublicIcon from '@mui/icons-material/Public';
import { jwtDecode } from 'jwt-decode';
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { API } from '../config/apiConfig';
import { useUtcMidnightCountdown, formatUtcMidnightCountdown } from '../hooks/useUtcMidnightCountdown';
import {
  dreameyDiamondsUserSum,
  dreameyDiamondsDisplayTotal,
  parseClaimedRankRewardIdsForDreameyVids,
  DREAMEY_VIDS_APP_ID,
  readStoredDownlineClaimKeys,
  readStoredDownlineClaims,
  writeStoredDownlineClaims,
  appendStoredDownlineClaim,
  mergeUniqueStrings,
  downlineCoinsForMilestoneDays,
} from '../utils/dreameyDiamonds';
import {
  readHomeCache,
  readUserSessionProfileCache,
  writeUserSessionProfileCache,
  mergeDashboardRawFields,
  mergeHomeCacheSelfInfo,
  markUserDashboardHomeRequiresFreshFetch,
  clearUserDashboardHomeForceNetwork,
} from '../utils/userDashboardHomeCache';

function getUserId() {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  if (token) {
    try {
      const dec = jwtDecode(token);
      return dec.userId ?? dec.UserID ?? dec.sub ?? dec.id ?? null;
    } catch {
      return null;
    }
  }
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  return user?.userId ?? user?.UserID ?? user?.id ?? null;
}

/** Home dashboard bundle (6h); only for viewing own activity as the logged-in user. */
function getOwnDashboardCacheBundle(userId, loggedInUserId, isViewingOwnActivity) {
  if (!isViewingOwnActivity || loggedInUserId == null || userId == null) return null;
  if (Number(loggedInUserId) !== Number(userId)) return null;
  return readHomeCache(userId);
}

function getOwnDashboardRawBundle(userId, loggedInUserId, isViewingOwnActivity) {
  return getOwnDashboardCacheBundle(userId, loggedInUserId, isViewingOwnActivity)?.raw ?? null;
}

const DAILY_TARGET = 5;

/** My Activity profile header: set true to show the circular refresh button again. */
const SHOW_ACTIVITY_PROFILE_REFRESH_BUTTON = false;

/** Days required before milestone prize eligibility / claims may apply (program rule). */
const STREAK_MILESTONE_DAYS = 365;

const STREAK_CLAIMS_STORAGE_PREFIX = 'streak365Claims_';

const RANK_CLAIMS_STORAGE_PREFIX = 'rankDiamondClaims_';

/** Parent earns Dreamey Diamonds per direct referral when that member hits each streak milestone (program rules). */

const MIN_DOWNLINE_FOR_DREAMEY_STREAK = 2;

const DOWNLINE_STREAK_DREAMEY_MILESTONES = [
  { days: 30, coins: 10 },
  { days: 90, coins: 75 },
  { days: 180, coins: 200 },
  { days: 365, coins: 500 },
];

function formatDownlineMilestoneLabel(milestone) {
  return `${milestone.days} Days - ${milestone.coins} 💎`;
}

/** Shown on Activity: one-time Diamond bonus when you achieve the rank (level + direct referrals). */
const RANK_DIAMOND_BONUSES = [
  {
    id: 'rank-diamond-exec',
    rank: 'Diamond Executive',
    levelMin: 15,
    levelReq: 'Completing level 15',
    directReferrals: 10,
    bonus: '2,500 Diamonds',
  },
  {
    id: 'rank-gold-exec',
    rank: 'Gold Executive',
    levelMin: 12,
    levelReq: 'Completing level 12',
    directReferrals: 8,
    bonus: '1,500 Diamonds',
  },
  {
    id: 'rank-silver-exec',
    rank: 'Silver Executive',
    levelMin: 9,
    levelReq: 'Completing level 9',
    directReferrals: 6,
    bonus: '500 Diamonds',
  },
  {
    id: 'rank-team-leader',
    rank: 'Team Leader',
    levelMin: 6,
    levelReq: 'Completing level 6',
    directReferrals: 4,
    bonus: '250 Diamonds',
  },
  {
    id: 'rank-leader',
    /** Depth = max downline levels under you (same as Community Network / DB designation). */
    rank: 'Leader',
    levelMin: 3,
    levelReq: 'Completing level 3',
    directReferrals: 2,
    bonus: '100 Diamonds',
  },
];

function parseRankBonusDiamondAmount(bonusLabel) {
  const m = String(bonusLabel).replace(/,/g, '').match(/(\d+)/);
  return m ? Number(m[1]) : 0;
}

function totalRankLadderDiamondsCap() {
  return RANK_DIAMOND_BONUSES.reduce((acc, row) => acc + parseRankBonusDiamondAmount(row.bonus), 0);
}

/** One-time Diamond total for rank tiers the user currently qualifies for (level + refs + profile). */
function qualifiedRankDiamondsTotal(userLevel, directRefCount, profileOk, referralsDataReady) {
  if (!referralsDataReady || !profileOk) return 0;
  let sum = 0;
  for (const row of RANK_DIAMOND_BONUSES) {
    if (userLevel >= row.levelMin && directRefCount >= row.directReferrals) {
      sum += parseRankBonusDiamondAmount(row.bonus);
    }
  }
  return sum;
}

/** How many rank rows (Leader → Diamond Executive) the user currently qualifies for. */
function countQualifiedRankTiers(userLevel, directRefCount, profileOk, referralsDataReady) {
  if (!referralsDataReady || !profileOk) return 0;
  return RANK_DIAMOND_BONUSES.filter(
    (row) => userLevel >= row.levelMin && directRefCount >= row.directReferrals
  ).length;
}

const RANK_DIAMOND_TIER_TOTAL = RANK_DIAMOND_BONUSES.length;

/** Left → right: Leader through Diamond Executive (five equal segments on the bar). */
const RANK_BAR_SEGMENTS = [...RANK_DIAMOND_BONUSES].reverse();

/** Depth thresholds left → right (same order as bar segments). */
const RANK_BAR_DEPTH_THRESHOLDS = RANK_BAR_SEGMENTS.map((row) => row.levelMin);

/** Direct-referral thresholds left → right (Leader → Diamond Executive). */
const RANK_BAR_REFERRAL_THRESHOLDS = RANK_BAR_SEGMENTS.map((row) => row.directReferrals);

/**
 * Continuous bar fill for one metric: five equal segments (Leader→Diamond); within each segment,
 * progress is linear between consecutive thresholds (e.g. depth 4 → 20% + ⅓×20% ≈ 26.7%).
 */
function rankMetricToBarFillPercent(rawValue, thresholdsAscending) {
  const d = Math.max(0, Number(rawValue) || 0);
  const tiers = thresholdsAscending;
  const n = tiers.length;
  if (n === 0) return 0;
  const segW = 100 / n;
  if (d >= tiers[n - 1]) return 100;
  if (d < tiers[0]) return tiers[0] > 0 ? (d / tiers[0]) * segW : 0;
  for (let k = 0; k < n - 1; k++) {
    if (d >= tiers[k] && d < tiers[k + 1]) {
      const span = tiers[k + 1] - tiers[k];
      const t = span > 0 ? (d - tiers[k]) / span : 0;
      return (k + 1) * segW + t * segW;
    }
  }
  return 100;
}

function rankLevelsToBarFillPercent(level) {
  return rankMetricToBarFillPercent(level, RANK_BAR_DEPTH_THRESHOLDS);
}

function rankReferralsToBarFillPercent(directRefCount) {
  return rankMetricToBarFillPercent(directRefCount, RANK_BAR_REFERRAL_THRESHOLDS);
}

/**
 * Highest bar segment index (0 = Leader … 4 = Diamond) where both levelMin and directReferrals
 * are satisfied. Tiers are nested, so qualification is contiguous from the left.
 */
function highestQualifiedRankBarSegmentIndex(userLevel, directRefCount) {
  const lv = Math.max(0, Number(userLevel) || 0);
  const refs = Math.max(0, Number(directRefCount) || 0);
  let k = -1;
  for (let i = 0; i < RANK_BAR_SEGMENTS.length; i++) {
    const row = RANK_BAR_SEGMENTS[i];
    if (lv >= row.levelMin && refs >= row.directReferrals) {
      k = i;
    }
  }
  return k;
}

/**
 * Highest Dreamey Vids rank title from the same ladder as Ranks & Diamonds (Leader → Diamond Executive).
 * Uses the same readiness + profile gates as qualified rank Diamond totals so the header chip matches the ladder.
 */
function highestQualifiedRankNameForDisplay(userLevel, directRefCount, profileOk, referralsDataReady) {
  if (!referralsDataReady || !profileOk) return null;
  const k = highestQualifiedRankBarSegmentIndex(userLevel, directRefCount);
  if (k < 0) return null;
  return RANK_BAR_SEGMENTS[k]?.rank ?? null;
}

/** Highest segment index satisfied by one metric only (levels or referrals bar labels). */
function highestMetricBarSegmentIndex(rawValue, thresholdKey) {
  const v = Math.max(0, Number(rawValue) || 0);
  let k = -1;
  for (let i = 0; i < RANK_BAR_SEGMENTS.length; i++) {
    const t = RANK_BAR_SEGMENTS[i][thresholdKey];
    if (v >= t) {
      k = i;
    }
  }
  return k;
}

/** Reveal bar labels and diamond/claim row through the next tier only: segments 0..k+1 (at least 0). */
function rankBarSegmentLabelVisible(segmentIndex, highestQualifiedIndex) {
  const lastVisible = Math.min(
    highestQualifiedIndex + 1,
    RANK_BAR_SEGMENTS.length - 1
  );
  return segmentIndex <= lastVisible;
}

/** Shorter tier name for lock hints, e.g. "Silver Executive" → "Silver". */
function rankTierShortNameForHint(fullRank) {
  if (fullRank === 'Team Leader') return 'Team Leader';
  if (fullRank === 'Leader') return 'Leader';
  const m = String(fullRank).match(/^(\w+)\s+Executive$/);
  if (m) return m[1];
  return fullRank;
}

/** Copy for columns not yet unlocked: prerequisite is the previous tier on the ladder. */
function rankLockedColumnHint(segmentIndex) {
  if (segmentIndex < 1) return '';
  const prev = RANK_BAR_SEGMENTS[segmentIndex - 1];
  return `Complete ${rankTierShortNameForHint(prev.rank)}`;
}

/** Title row + progress bar with centered in-bar label (no separate scale row). */
function ProgressMilestoneTrack({
  theme,
  title,
  titleEndAdornment = null,
  ariaLabel,
  fillPercent,
  centerLabel,
  outerBg,
  centerLabelColor,
  barBackground,
  subtitle,
  barEndAdornment = null,
}) {
  const fp = Math.min(100, Math.max(0, fillPercent));
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: alpha(theme.palette.divider, 0.85),
        bgcolor: outerBg,
      }}
    >
      <Box sx={{ position: 'relative', mb: 0.5 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontWeight: 700,
            letterSpacing: '0.08em',
            display: 'block',
            lineHeight: 1.2,
            pr: titleEndAdornment ? 4.5 : 0,
          }}
        >
          {title}
        </Typography>
        {titleEndAdornment ? (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              right: 0,
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {titleEndAdornment}
          </Box>
        ) : null}
      </Box>
      <Box
        role="img"
        aria-label={ariaLabel}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            width: 3,
            height: 22,
            borderRadius: 0.5,
            bgcolor: alpha(theme.palette.text.secondary, 0.85),
            flexShrink: 0,
          }}
        />
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            height: 22,
            borderRadius: 11,
            bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.12 : 0.08),
            border: '1px solid',
            borderColor: alpha(theme.palette.divider, 0.9),
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${fp}%`,
              borderRadius: 'inherit',
              background: barBackground,
              transition: 'width 0.45s ease-out',
              zIndex: 0,
            }}
          />
          {centerLabel ? (
            <Typography
              variant="caption"
              component="span"
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: { xs: '0.7rem', sm: '0.78rem' },
                color: centerLabelColor,
                textShadow:
                  theme.palette.mode === 'dark'
                    ? '0 0 8px rgba(0,0,0,0.92), 0 1px 2px rgba(0,0,0,1)'
                    : '0 0 6px rgba(255,255,255,0.85), 0 1px 2px rgba(0,0,0,0.35)',
                zIndex: 1,
                pointerEvents: 'none',
                px: 0.5,
                textAlign: 'center',
                lineHeight: 1.1,
              }}
            >
              {centerLabel}
            </Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            width: 3,
            height: 22,
            borderRadius: 0.5,
            bgcolor: alpha(theme.palette.text.secondary, 0.85),
            flexShrink: 0,
          }}
        />
        {barEndAdornment ? (
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', ml: 0.25 }}>
            {barEndAdornment}
          </Box>
        ) : null}
      </Box>
      {subtitle ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', lineHeight: 1.5 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

/** Ranks & Diamonds: two parallel fills (levels + referrals); tier claim needs both. */
function RanksDiamondsSegmentedMilestoneTrack({
  theme,
  ariaLabel,
  summaryLine = '',
  outerBg,
  levelsBarBackground,
  referralsBarBackground,
  subtitle = '',
  claimedRankRewardIds = [],
  onClaimRank,
  rankClaimInFlight = false,
  userProgramLevel = 0,
  directRefCount = 0,
  rankProfileComplete = false,
  referralsDataReady = false,
  referralsFetchError = '',
}) {
  const fpLevels = Math.min(100, Math.max(0, rankLevelsToBarFillPercent(userProgramLevel)));
  const fpReferrals = Math.min(100, Math.max(0, rankReferralsToBarFillPercent(directRefCount)));
  const rankLabelThroughIndex = highestQualifiedRankBarSegmentIndex(userProgramLevel, directRefCount);
  const divider = alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.22 : 0.45);
  const showClaims = typeof onClaimRank === 'function';

  const labelShadow =
    theme.palette.mode === 'dark'
      ? '0 0 6px rgba(0,0,0,0.85), 0 1px 2px rgba(0,0,0,0.9)'
      : '0 0 4px rgba(255,255,255,0.6), 0 1px 2px rgba(0,0,0,0.35)';

  const metricLabelThroughIndex = {
    levels: highestMetricBarSegmentIndex(userProgramLevel, 'levelMin'),
    referrals: highestMetricBarSegmentIndex(directRefCount, 'directReferrals'),
  };

  const renderFiveSegmentBar = (fp, barBg, metricKind) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <Box
        sx={{
          width: 3,
          height: 22,
          borderRadius: 0.5,
          bgcolor: alpha(theme.palette.text.secondary, 0.85),
          flexShrink: 0,
        }}
      />
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          height: 22,
          borderRadius: 11,
          bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.12 : 0.08),
          border: '1px solid',
          borderColor: alpha(theme.palette.divider, 0.9),
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${fp}%`,
            borderRadius: 'inherit',
            background: barBg,
            transition: 'width 0.45s ease-out',
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        >
          {RANK_BAR_SEGMENTS.map((row, i) => {
            const showLabel = rankBarSegmentLabelVisible(
              i,
              metricLabelThroughIndex[metricKind]
            );
            const segmentLabel =
              metricKind === 'levels'
                ? `${row.levelMin} - Levels`
                : `${row.directReferrals} - Referrals`;
            return (
              <Box
                key={row.id}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  borderRight: i < 4 ? `1px solid ${divider}` : 'none',
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 0.125,
                }}
              >
                {showLabel ? (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '0.5rem', sm: '0.55rem' },
                      lineHeight: 1.05,
                      textAlign: 'center',
                      color: theme.palette.common.white,
                      textShadow: labelShadow,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                    }}
                  >
                    {segmentLabel}
                  </Typography>
                ) : (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '0.42rem', sm: '0.48rem' },
                      lineHeight: 1.15,
                      textAlign: 'center',
                      color: alpha(theme.palette.common.white, 0.5),
                      textShadow: labelShadow,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      maxWidth: '100%',
                      px: 0.125,
                    }}
                  >
                    {rankLockedColumnHint(i)}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box
        sx={{
          width: 3,
          height: 22,
          borderRadius: 0.5,
          bgcolor: alpha(theme.palette.text.secondary, 0.85),
          flexShrink: 0,
        }}
      />
    </Box>
  );

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: alpha(theme.palette.divider, 0.85),
        bgcolor: outerBg,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.5,
          mb: 1.1,
        }}
      >
        <Box sx={{ width: 3, flexShrink: 0 }} aria-hidden />
        <Stack direction="row" sx={{ flex: 1, minWidth: 0, alignItems: 'flex-start' }}>
          {RANK_BAR_SEGMENTS.map((row) => {
            const tierAchieved =
              rankClaimBlockedReason(
                row,
                userProgramLevel,
                directRefCount,
                rankProfileComplete,
                referralsDataReady,
                referralsFetchError
              ) === '';
            return (
              <Box
                key={`rank-top-${row.id}`}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  px: { xs: 0.125, sm: 0.25 },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '0.75rem', sm: '0.82rem', md: '0.875rem' },
                    lineHeight: 1.25,
                    display: 'inline-block',
                    maxWidth: '100%',
                    hyphens: 'auto',
                    color: tierAchieved ? theme.palette.success.main : 'text.secondary',
                    textAlign: 'center',
                  }}
                >
                  {row.rank}
                </Typography>
              </Box>
            );
          })}
        </Stack>
        <Box sx={{ width: 3, flexShrink: 0 }} aria-hidden />
      </Box>
      <Stack
        component="div"
        role="img"
        aria-label={ariaLabel}
        spacing={0.65}
        sx={{ mt: 0.25 }}
      >
        {renderFiveSegmentBar(fpLevels, levelsBarBackground, 'levels')}
        {renderFiveSegmentBar(fpReferrals, referralsBarBackground, 'referrals')}
      </Stack>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.5,
          mt: 1,
        }}
      >
        <Box sx={{ width: 3, flexShrink: 0 }} aria-hidden />
        <Stack direction="row" sx={{ flex: 1, minWidth: 0, alignItems: 'flex-start' }}>
        {RANK_BAR_SEGMENTS.map((row, i) => {
          const showRewardsRow = rankBarSegmentLabelVisible(i, rankLabelThroughIndex);
          const diamondNum = parseRankBonusDiamondAmount(row.bonus);
          const claimed = claimedRankRewardIds.includes(row.id);
          const tierBlock = rankClaimBlockedReason(
            row,
            userProgramLevel,
            directRefCount,
            rankProfileComplete,
            referralsDataReady,
            referralsFetchError
          );
          const seqBlock = rankPriorTierSequentialBlockReason(i, claimedRankRewardIds);
          const blockOrSeq = tierBlock || seqBlock;
          const canClaim = showClaims && blockOrSeq === '';
          const tierQualifiedForDiamonds = tierBlock === '';
          return (
            <Box
              key={row.id}
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                justifyContent: 'center',
                textAlign: 'center',
                px: { xs: 0.125, sm: 0.25 },
                borderRight: 'none',
              }}
            >
              {showRewardsRow ? (
                <Stack spacing={0.35} alignItems="center" sx={{ width: '100%' }}>
                  <Typography
                    variant="caption"
                    component="div"
                    aria-label={`${diamondNum.toLocaleString()} diamonds`}
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '0.68rem', sm: '0.75rem' },
                      color: tierQualifiedForDiamonds ? theme.palette.success.main : portal.pink,
                      lineHeight: 1.2,
                      display: 'block',
                    }}
                  >
                    {diamondNum.toLocaleString()}
                    <Box component="span" sx={{ ml: 0.25, fontWeight: 700 }} aria-hidden>
                      💎
                    </Box>
                  </Typography>
                  {showClaims ? (
                    claimed ? (
                      <Chip size="small" label="Claimed" color="success" variant="outlined" sx={{ height: 22, fontSize: '0.65rem' }} />
                    ) : canClaim ? (
                      <Box sx={{ width: '100%', maxWidth: 88 }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => onClaimRank(row.id, row.rank, row.bonus)}
                          disabled={rankClaimInFlight}
                          fullWidth
                          sx={{
                            fontSize: '0.65rem',
                            py: 0.25,
                            minWidth: 0,
                            ...(rankClaimInFlight
                              ? {
                                  opacity: 0.55,
                                  background: alpha(theme.palette.action.disabled, 0.35),
                                  color: theme.palette.action.disabled,
                                }
                              : {}),
                          }}
                        >
                          {rankClaimInFlight ? '…' : 'Claim'}
                        </Button>
                      </Box>
                    ) : (
                      <Tooltip title={blockOrSeq}>
                        <Box sx={{ width: '100%', maxWidth: 88 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled
                            fullWidth
                            sx={{ fontSize: '0.65rem', py: 0.25, minWidth: 0 }}
                          >
                            Locked
                          </Button>
                        </Box>
                      </Tooltip>
                    )
                  ) : null}
                </Stack>
              ) : (
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                    lineHeight: 1.25,
                    textAlign: 'center',
                    display: 'block',
                    width: '100%',
                    py: 0.5,
                    px: 0.25,
                    color: portal.pink,
                  }}
                >
                  {rankLockedColumnHint(i)}
                </Typography>
              )}
            </Box>
          );
        })}
        </Stack>
        <Box sx={{ width: 3, flexShrink: 0 }} aria-hidden />
      </Box>
      {summaryLine ? (
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            display: 'block',
            textAlign: 'center',
            fontWeight: 700,
            color: portal.pink,
          }}
        >
          {summaryLine}
        </Typography>
      ) : null}
      {subtitle ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.25, display: 'block', lineHeight: 1.5 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

/** Possible milestone prizes after 365 consecutive days at the daily goal — limited pool; not all participants receive an award (program rules apply). */
const STREAK_365_REWARD_ROWS = [
  { id: 'thailand-return', reward: 'Thailand return', type: 'ticket', eligibilityKey: 'thailand_exception' },
  { id: 'maldives-return', reward: 'Maldives return', type: 'ticket', eligibilityKey: 'all' },
  { id: 'umrah-return', reward: 'Umrah return', type: 'ticket', eligibilityKey: 'turkey_my_ipb' },
  { id: 'ziarat-return', reward: 'Ziarat return', type: 'ticket', eligibilityKey: 'ipb' },
  { id: 'weekend-trip', reward: '3 days / 2 nights weekend trip', type: 'locals', eligibilityKey: 'turkey_my_in_pk' },
  { id: 'cash-1000', reward: 'Up to $1,000 reward', type: 'bank', eligibilityKey: 'all' },
  { id: 'diamonds-1000-app', reward: 'Up to 1,000 Diamonds', type: 'app', eligibilityKey: 'all' },
  { id: 'gift-hampers', reward: 'Gift hampers', type: null, eligibilityKey: 'all' },
];

function readStoredStreakClaims(userId) {
  if (userId == null) return [];
  try {
    const raw = localStorage.getItem(`${STREAK_CLAIMS_STORAGE_PREFIX}${userId}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function writeStoredStreakClaims(userId, ids) {
  if (userId == null) return;
  try {
    localStorage.setItem(`${STREAK_CLAIMS_STORAGE_PREFIX}${userId}`, JSON.stringify(ids));
  } catch {
    /* ignore quota / private mode */
  }
}

function writeStoredRankClaims(userId, ids) {
  if (userId == null) return;
  try {
    localStorage.setItem(`${RANK_CLAIMS_STORAGE_PREFIX}${userId}`, JSON.stringify(ids));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Claim identity from server: referral + milestone + reward cycle (resets after streak break or 365 completion). */
function downlineDreameyClaimKey(refUserId, milestoneDays, rewardCycle) {
  return `${Number(refUserId)}_${milestoneDays}_${Number(rewardCycle)}`;
}

function getPrevDownlineMilestoneDays(days) {
  const order = DOWNLINE_STREAK_DREAMEY_MILESTONES.map((x) => x.days);
  const idx = order.indexOf(days);
  if (idx <= 0) return null;
  return order[idx - 1];
}

function getDownlineCycleRowForRef(cycleState, refUserId) {
  const id = Number(refUserId);
  const row = (cycleState || []).find((r) => Number(r.referredUserId) === id);
  return (
    row || {
      referredUserId: id,
      rewardCycle: 1,
      baselineStreak: 0,
      lastSeenCurrentStreak: 0,
    }
  );
}

function isPrevDownlineMilestoneClaimed(claimedKeys, refUserId, milestoneDays, rewardCycle) {
  const prev = getPrevDownlineMilestoneDays(milestoneDays);
  if (prev == null) return true;
  const k = downlineDreameyClaimKey(refUserId, prev, rewardCycle);
  return claimedKeys.includes(k);
}

/** Home referral rows (per-ref /ads/streak) can be newer than raw.referralsActivity from streak-completers. */
function mergeReferralProgressWithHomeStreakRows(referralsProgress, homeRows) {
  if (!referralsProgress || !Array.isArray(homeRows) || homeRows.length === 0) {
    return referralsProgress;
  }
  const homeById = new Map(
    homeRows
      .filter((row) => Number.isFinite(Number(row?.userId)))
      .map((row) => [Number(row.userId), row])
  );
  if (homeById.size === 0) return referralsProgress;
  const list = Array.isArray(referralsProgress.referrals) ? referralsProgress.referrals : [];
  const referrals = list.map((progressRow) => {
    const home = homeById.get(Number(progressRow.userId));
    if (!home) return progressRow;
    const progCur = Math.max(0, Number(progressRow.currentStreak ?? progressRow.CurrentStreak ?? 0) || 0);
    const progLong = Math.max(0, Number(progressRow.longestStreak ?? progressRow.LongestStreak ?? 0) || 0);
    const homeCurRaw = home.currentStreak ?? home.CurrentStreak;
    const homeLongRaw = home.longestStreak ?? home.LongestStreak;
    const homeCur =
      homeCurRaw != null && Number.isFinite(Number(homeCurRaw))
        ? Math.max(0, Number(homeCurRaw) || 0)
        : null;
    const homeLong =
      homeLongRaw != null && Number.isFinite(Number(homeLongRaw))
        ? Math.max(0, Number(homeLongRaw) || 0)
        : null;
    const cur = homeCur != null ? Math.max(progCur, homeCur) : progCur;
    const longest =
      homeLong != null ? Math.max(progLong, homeLong, cur) : Math.max(progLong, cur);
    if (cur === progCur && longest === progLong) return progressRow;
    return { ...progressRow, currentStreak: cur, longestStreak: longest };
  });
  return { ...referralsProgress, referrals };
}

function bestReferralStreakFromProgress(progressMatch) {
  if (!progressMatch || typeof progressMatch !== 'object') return 0;
  const cur = Math.max(0, Number(progressMatch.currentStreak ?? progressMatch.CurrentStreak ?? 0) || 0);
  const longest = Math.max(0, Number(progressMatch.longestStreak ?? progressMatch.LongestStreak ?? 0) || 0);
  return Math.max(cur, longest);
}

/** Matches server eligibility: main_ClaimDownlineStreakDiamond uses current streak vs baseline. */
function referralCurrentStreakFromProgress(progressMatch) {
  if (!progressMatch || typeof progressMatch !== 'object') return 0;
  return Math.max(0, Number(progressMatch.currentStreak ?? progressMatch.CurrentStreak ?? 0) || 0);
}

function extractNumericLevelFromRecord(obj) {
  if (!obj || typeof obj !== 'object') return null;
  const keys = [
    'Level',
    'level',
    'UserLevel',
    'userLevel',
    'CurrentLevel',
    'currentLevel',
    'MarketingLevel',
    'marketingLevel',
    'ReferralProgramLevel',
    'referralProgramLevel',
  ];
  for (const k of keys) {
    if (obj[k] == null || obj[k] === '') continue;
    const n = Number(obj[k]);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return null;
}

function resolveUserProgramLevel(profileUser, jwtPayload, levelByUserId, selfUserId) {
  const fromMap =
    selfUserId != null && levelByUserId && typeof levelByUserId === 'object'
      ? levelByUserId[Number(selfUserId)]
      : null;
  const fromMapNum = fromMap != null ? Number(fromMap) : NaN;
  const merged = {
    ...(profileUser && typeof profileUser === 'object' ? profileUser : {}),
    ...(jwtPayload && typeof jwtPayload === 'object' ? jwtPayload : {}),
  };
  const fromProfile = extractNumericLevelFromRecord(merged);
  const candidates = [];
  if (Number.isFinite(fromMapNum)) candidates.push(fromMapNum);
  if (fromProfile != null && Number.isFinite(fromProfile)) candidates.push(fromProfile);
  if (candidates.length === 0) return 0;
  return Math.max(...candidates);
}

function isProfileCompleteForRanks(profileUser) {
  if (!profileUser || typeof profileUser !== 'object') return false;
  const first = String(profileUser.FirstName ?? profileUser.firstName ?? '').trim();
  const last = String(profileUser.LastName ?? profileUser.lastName ?? '').trim();
  const email = String(profileUser.Email ?? profileUser.email ?? '').trim();
  return Boolean(first && last && email);
}

/** When true, placement depth matches SQL binary-complete tiers (2^d per tier). When false (default), matches production: deepest Level in tree (max chain depth). */
const USE_BINARY_COMPLETE_PLACEMENT =
  process.env.REACT_APP_USE_BINARY_COMPLETE_PLACEMENT === 'true';

/** Deepest Level in flat `referrals` response — same as production / dv-apps before binary-complete experiment. */
function maxChainPlacementDepthFromFlat(flat) {
  if (!Array.isArray(flat) || flat.length === 0) return 0;
  let m = 0;
  for (const r of flat) {
    const lv = Number(r.Level ?? r.level ?? 0) || 0;
    if (lv > m) m = lv;
  }
  return m;
}

/** Deepest nested level under referral roots (API `children` shape) — paired with max-chain mode. */
function maxDepthFromNestedReferralNodes(nodes, depth = 1) {
  if (!Array.isArray(nodes) || nodes.length === 0) return 0;
  let best = depth;
  for (const n of nodes) {
    const children = n.children;
    if (Array.isArray(children) && children.length > 0) {
      best = Math.max(best, maxDepthFromNestedReferralNodes(children, depth + 1));
    }
  }
  return best;
}

/**
 * Largest D such that placement tier 1..D each has exactly 2^D users (2-wide tree),
 * matching process.mlm_GetUserDesignationMetrics when that SQL mode is deployed.
 */
function maxBinaryCompletePlacementDepthFromFlat(flat) {
  if (!Array.isArray(flat) || flat.length === 0) return 0;
  const counts = new Map();
  for (const r of flat) {
    const lv = Number(r.Level ?? r.level ?? 0) || 0;
    if (lv < 1 || lv > 15) continue;
    counts.set(lv, (counts.get(lv) || 0) + 1);
  }
  for (let d = 1; d <= 15; d += 1) {
    const need = 2 ** d;
    const have = counts.get(d) ?? 0;
    if (have !== need) return d - 1;
  }
  return 15;
}

/**
 * Same binary-complete rule as flat, for nested `referrals` fallback (BFS by tier).
 */
function maxBinaryCompletePlacementDepthFromNested(nodes) {
  if (!Array.isArray(nodes) || nodes.length === 0) return 0;
  let frontier = nodes;
  for (let d = 1; d <= 15; d += 1) {
    const need = 2 ** d;
    if (frontier.length !== need) return d - 1;
    const next = [];
    for (const n of frontier) {
      const ch = n.children;
      if (Array.isArray(ch)) next.push(...ch);
    }
    frontier = next;
  }
  return 15;
}

/**
 * Placement depth for Ranks / Network Lv chip.
 * Default: max chain depth (matches live production). Opt-in binary-complete via env.
 */
function maxPlacementDepthFromDirectReferralsResponse(data) {
  if (!data || typeof data !== 'object') return 0;
  const flat = data.flat;
  if (Array.isArray(flat) && flat.length > 0) {
    return USE_BINARY_COMPLETE_PLACEMENT
      ? maxBinaryCompletePlacementDepthFromFlat(flat)
      : maxChainPlacementDepthFromFlat(flat);
  }
  const refs = data.referrals;
  if (!Array.isArray(refs) || refs.length === 0) return 0;
  return USE_BINARY_COMPLETE_PLACEMENT
    ? maxBinaryCompletePlacementDepthFromNested(refs)
    : maxDepthFromNestedReferralNodes(refs, 1);
}

/** Leader → Diamond order matches RANK_BAR_SEGMENTS index (0 = Leader). */
function rankPriorTierSequentialBlockReason(segmentIndexBar, claimedRankRewardIds) {
  if (segmentIndexBar <= 0) return '';
  const prev = RANK_BAR_SEGMENTS[segmentIndexBar - 1];
  if (claimedRankRewardIds.includes(prev.id)) return '';
  return `Claim ${prev.rank} first.`;
}

/** RANK_DIAMOND_BONUSES index: 0 = Diamond Executive … 4 = Leader (table order in dialog). */
function rankDialogPriorSequentialBlockReason(dialogBonusIndex, claimedRankRewardIds) {
  if (dialogBonusIndex >= RANK_DIAMOND_BONUSES.length - 1) return '';
  const prev = RANK_DIAMOND_BONUSES[dialogBonusIndex + 1];
  if (claimedRankRewardIds.includes(prev.id)) return '';
  return `Claim ${prev.rank} first.`;
}

function rankClaimBlockedReason(
  row,
  userLevel,
  directRefCount,
  profileOk,
  referralsDataReady,
  referralsFetchError
) {
  if (referralsFetchError) {
    return `Could not load referrals: ${referralsFetchError}. Use Refresh on Activity, then try again.`;
  }
  if (!referralsDataReady) {
    return 'Loading referral data…';
  }
  if (!profileOk) {
    return 'Complete your profile (first name, last name, and email) to qualify for rank bonuses.';
  }
  if (userLevel < row.levelMin) {
    const leaderTip =
      row.id === 'rank-leader'
        ? USE_BINARY_COMPLETE_PLACEMENT
          ? ' Same as Community “Network Lv” (binary-complete tiers).'
          : ' Same as Community “Network Lv” (max downline depth).'
        : '';
    return `Need depth ≥${row.levelMin} · yours ${userLevel}.${leaderTip}`;
  }
  if (directRefCount < row.directReferrals) {
    return `Need ${row.directReferrals} direct referrals (you have ${directRefCount}).`;
  }
  return '';
}

function bestStreakFromData(data) {
  if (!data || typeof data !== 'object') return 0;
  const cur = Math.max(0, Number(data.currentStreak ?? data.CurrentStreak ?? 0) || 0);
  const longest = Math.max(0, Number(data.longestStreak ?? data.LongestStreak ?? 0) || 0);
  return Math.max(cur, longest);
}

function streakRewardTypeLabel(type) {
  switch (type) {
    case 'ticket':
      return 'Ticket only';
    case 'locals':
      return 'Locals only';
    case 'bank':
      return 'Bank transfer';
    case 'app':
      return 'App';
    default:
      return '—';
  }
}

/** Display line for streak table, e.g. ticket rows show place only ("Maldives"); "Weekend Trip, Locals" for locals. */
function streakRewardDisplayLabel(row) {
  if (row.type == null) return row.reward;
  if (row.type === 'ticket') {
    const place = String(row.reward)
      .replace(/\s+return$/i, '')
      .trim();
    return place || row.reward;
  }
  if (row.type === 'locals') {
    return 'Weekend Trip, Locals';
  }
  if (row.type === 'bank') {
    return String(row.reward)
      .replace(/\s+reward\s*$/i, '')
      .trim();
  }
  if (row.type === 'app') {
    return row.reward;
  }
  return `${row.reward}, ${streakRewardTypeLabel(row.type)}`;
}

function streak365EligibilitySummary(eligibilityKey) {
  switch (eligibilityKey) {
    case 'all':
      return 'All countries';
    case 'thailand_exception':
      return 'All except India, Pakistan & Bangladesh';
    case 'turkey_my_ipb':
      return 'Turkey, Malaysia, Pakistan, India & Bangladesh';
    case 'ipb':
      return 'India, Pakistan & Bangladesh';
    case 'turkey_my_in_pk':
      return 'Turkey, Malaysia, India & Pakistan';
    default:
      return '—';
  }
}

function Streak365EligibilityTooltip({ eligibilityKey }) {
  return (
    <Stack spacing={0.35} sx={{ py: 0.25 }}>
      <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.4, opacity: 0.85 }}>
        Eligible countries
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.45 }}>
        {streak365EligibilitySummary(eligibilityKey)}
      </Typography>
    </Stack>
  );
}


function isAdminRecord(obj) {
  if (!obj || typeof obj !== 'object') return false;
  const tid = Number(
    obj.UserTypeId ?? obj.userTypeId ?? obj.UserTypeID ?? obj.userTypeID
  );
  if (tid === 1) return true;
  const name = String(obj.UserTypeName ?? obj.userTypeName ?? '').trim().toLowerCase();
  if (name === 'admin' || name === 'administrator') return true;
  return false;
}

/** Matches App.js: director accounts use userTypeId === 4. */
function isDirectorRecord(obj) {
  if (!obj || typeof obj !== 'object') return false;
  const tid = Number(
    obj.UserTypeId ?? obj.userTypeId ?? obj.UserTypeID ?? obj.userTypeID
  );
  if (tid === 4) return true;
  const name = String(obj.UserTypeName ?? obj.userTypeName ?? '').trim().toLowerCase();
  if (name === 'director') return true;
  return false;
}

/**
 * Explicit MLM / rank titles only (never UserTypeName — that is handled by isAdminRecord / isDirectorRecord).
 */
function explicitLeaderRankFromRecord(obj) {
  if (!obj || typeof obj !== 'object') return '';
  const candidates = [
    obj.DesignationName,
    obj.designationName,
    obj.ReferralRank,
    obj.referralRank,
    obj.RankName,
    obj.rankName,
    obj.RankTitle,
    obj.rankTitle,
    obj.UserRank,
    obj.userRank,
    obj.MLMRank,
    obj.mlmRank,
    obj.ReferralTitle,
    obj.referralTitle,
    obj.Title,
    obj.title,
  ];
  for (const v of candidates) {
    if (v == null) continue;
    const s = String(v).trim();
    if (!s) continue;
    const lower = s.toLowerCase();
    if (
      lower === 'user' ||
      lower === 'member' ||
      lower === 'standard' ||
      lower === 'admin' ||
      lower === 'administrator' ||
      lower === 'director'
    ) {
      continue;
    }
    return s;
  }
  return '';
}

function resolveLeaderLabel(mergedRecord, designationByUserId, userId, { defaultLeader }) {
  let leader = explicitLeaderRankFromRecord(mergedRecord);
  if (!leader && userId != null && designationByUserId && typeof designationByUserId === 'object') {
    const dn = String(designationByUserId[Number(userId)] ?? '').trim();
    if (dn && dn.toLowerCase() !== 'null') leader = dn;
  }
  if (!leader && defaultLeader) {
    leader = 'Leader';
  }
  if (!leader && !defaultLeader) {
    leader = 'Member';
  }
  return leader;
}

/** Role chips (Admin, Director) + leader / tree rank when each applies. */
function getActivityRankChipsForSelf(
  profileUser,
  jwtPayload,
  designationByUserId,
  selfUserId,
  hasDirectReferrals,
  ladderRankOverride = null
) {
  const chips = [];
  if (isAdminRecord(profileUser) || isAdminRecord(jwtPayload)) {
    chips.push('Admin');
  }
  if (isDirectorRecord(profileUser) || isDirectorRecord(jwtPayload)) {
    if (!chips.includes('Director')) {
      chips.push('Director');
    }
  }

  const merged = profileUser && typeof profileUser === 'object' ? profileUser : {};
  const jwtPart = jwtPayload && typeof jwtPayload === 'object' ? jwtPayload : {};
  const ladder = ladderRankOverride != null ? String(ladderRankOverride).trim() : '';
  const leader = ladder
    ? ladder
    : resolveLeaderLabel({ ...merged, ...jwtPart }, designationByUserId, selfUserId, {
        defaultLeader: hasDirectReferrals,
      });
  if (leader && !chips.includes(leader)) {
    chips.push(leader);
  }
  return chips;
}

function getActivityRankChipsForReferral(ref, progressMatch, designationByUserId) {
  const merged = { ...ref, ...(progressMatch || {}) };
  const chips = [];
  if (isAdminRecord(merged)) {
    chips.push('Admin');
  }
  if (isDirectorRecord(merged)) {
    if (!chips.includes('Director')) {
      chips.push('Director');
    }
  }
  const uid = ref?.UserID ?? ref?.userId ?? merged.UserID ?? merged.userId;
  const leader = resolveLeaderLabel(merged, designationByUserId, uid, { defaultLeader: false });
  if (leader && !chips.includes(leader)) {
    chips.push(leader);
  }
  return chips;
}

function rankChipSx(label, theme) {
  const a = (color, v) => alpha(color, v);
  switch (label) {
    case 'Admin':
      return {
        fontWeight: 700,
        border: '1px solid',
        borderColor: a(theme.palette.error.main, 0.45),
        bgcolor: a(theme.palette.error.main, 0.12),
        color: theme.palette.error.light,
        boxShadow: `0 0 20px ${a(theme.palette.error.main, 0.12)}`,
      };
    case 'Director':
      return {
        fontWeight: 700,
        border: '1px solid',
        borderColor: a(theme.palette.warning.main, 0.45),
        bgcolor: a(theme.palette.warning.main, 0.12),
        color: theme.palette.warning.light,
        boxShadow: `0 0 20px ${a(theme.palette.warning.main, 0.12)}`,
      };
    default:
      return {
        fontWeight: 700,
        border: '1px solid',
        borderColor: a(portal.rose, 0.45),
        bgcolor: a(portal.rose, 0.12),
        color: portal.pink,
        boxShadow: `0 0 16px ${a(portal.rose, 0.15)}`,
      };
  }
}

/** Backend field names may vary; normalize for display. */
function normalizeStreakPayload(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw.data && typeof raw.data === 'object' ? raw.data : raw;
  const last30 =
    r.last30Days ??
    r.Last30Days ??
    r.last_30_days ??
    [];
  return {
    adsWatchedToday: Number(
      r.adsWatchedToday ??
        r.adsToday ??
        r.AdsWatchedToday ??
        r.watchedToday ??
        0
    ),
    goalPerDay: Number(
      r.goalPerDay ?? r.goal ?? r.GoalPerDay ?? r.dailyGoal ?? DAILY_TARGET
    ),
    currentStreak: Number(
      r.currentStreak ?? r.CurrentStreak ?? r.consecutiveDays ?? 0
    ),
    longestStreak: Number(
      r.longestStreak ?? r.LongestStreak ?? r.bestStreak ?? 0
    ),
    last30Days: Array.isArray(last30) ? last30 : [],
  };
}

/**
 * One row from /referrals/streak-completers (or similar). Community tree uses a different payload
 * (directReferrals + adsWatched); we normalize IDs and fields so Activity cards match.
 */
function normalizeReferralProgressFromApiRow(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const uid = raw.userId ?? raw.UserID ?? raw.userID;
  if (uid == null) return null;
  const userIdNum = Number(uid);
  if (!Number.isFinite(userIdNum)) return null;
  const fromStreak = normalizeStreakPayload(raw);
  if (fromStreak) {
    return {
      userId: userIdNum,
      adsWatchedToday: fromStreak.adsWatchedToday,
      currentStreak: fromStreak.currentStreak,
      longestStreak: fromStreak.longestStreak,
      goalPerDay: fromStreak.goalPerDay,
    };
  }
  return {
    userId: userIdNum,
    adsWatchedToday: Number(raw.adsWatchedToday ?? raw.AdsWatchedToday ?? raw.adsToday ?? 0) || 0,
    currentStreak: Number(raw.currentStreak ?? raw.CurrentStreak ?? 0) || 0,
    longestStreak: Number(raw.longestStreak ?? raw.LongestStreak ?? 0) || 0,
    goalPerDay: Number(raw.goalPerDay ?? raw.GoalPerDay ?? raw.dailyGoal ?? DAILY_TARGET) || DAILY_TARGET,
  };
}

export default function AdsWatchedStreak({ view = 'all' }) {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loggedInUserId = useMemo(() => getUserId(), []);
  const queryMemberId = useMemo(() => {
    const raw = searchParams.get('userId') ?? searchParams.get('ref');
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);
  const userId = view === 'users' ? loggedInUserId : (queryMemberId ?? loggedInUserId);
  const showMyActivity = view !== 'users';
  const isViewingOwnActivity = useMemo(
    () =>
      loggedInUserId != null &&
      userId != null &&
      Number(loggedInUserId) === Number(userId),
    [loggedInUserId, userId]
  );
  const showUsersActivity = view !== 'my';
  const jwtPayload = useMemo(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    if (!token) return null;
    try {
      return jwtDecode(token);
    } catch {
      return null;
    }
  }, []);
  const utcMidnightRemainingMs = useUtcMidnightCountdown();
  const [streakData, setStreakData] = useState(null);
  const [loadingStreak, setLoadingStreak] = useState(false);
  const [errStreak, setErrStreak] = useState('');
  const [directReferrals, setDirectReferrals] = useState([]);
  const [referralsProgress, setReferralsProgress] = useState(null);
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  const [errReferrals, setErrReferrals] = useState('');
  const [referralLevelByUserId, setReferralLevelByUserId] = useState({});
  const [referralDesignationByUserId, setReferralDesignationByUserId] = useState({});
  const [myProfileUser, setMyProfileUser] = useState(null);
  const [streakRewardsDialogOpen, setStreakRewardsDialogOpen] = useState(false);
  /** When opened from Home, Close returns here instead of staying on My Activity. */
  const streakRewardsReturnPathRef = useRef(null);
  /** Which panel the info dialog shows: from "Your progress" vs Streak card. */
  const [progressInfoDialogSection, setProgressInfoDialogSection] = useState('ranks');
  const [claimedStreakRewardIds, setClaimedStreakRewardIds] = useState([]);
  const [claimedRankRewardIds, setClaimedRankRewardIds] = useState([]);
  /** `${refUserId}_${milestoneDays}_${rewardCycle}` — server-backed downline streak claims */
  const [claimedDownlineStreakKeys, setClaimedDownlineStreakKeys] = useState([]);
  const [downlineCycleState, setDownlineCycleState] = useState([]);
  const [loadingDownlineStreak, setLoadingDownlineStreak] = useState(false);
  const [errDownlineStreak, setErrDownlineStreak] = useState('');
  const [claimSnackbar, setClaimSnackbar] = useState({ open: false, message: '' });
  const [rankClaimInFlight, setRankClaimInFlight] = useState(false);
  const [downlineClaimInFlight, setDownlineClaimInFlight] = useState(false);
  /** Max downline depth from GET /referrals/direct/:code (`flat` / nested) — same metric as Community Network. */
  const [placementMaxDepth, setPlacementMaxDepth] = useState(null);
  const [loadingPlacementDepth, setLoadingPlacementDepth] = useState(false);
  const [errPlacementDepth, setErrPlacementDepth] = useState('');
  const [dreameyDiamondsTotal, setDreameyDiamondsTotal] = useState(null);

  const fetchStreak = useCallback(async () => {
    if (!userId) return;
    const raw = getOwnDashboardRawBundle(userId, loggedInUserId, isViewingOwnActivity);
    if (raw?.streak) {
      setStreakData(normalizeStreakPayload(raw.streak) ?? raw.streak);
      setLoadingStreak(false);
      setErrStreak('');
      return;
    }
    if (isViewingOwnActivity) {
      setLoadingStreak(false);
      setErrStreak('');
      setStreakData(null);
      return;
    }
    setLoadingStreak(true);
    setErrStreak('');
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
      const url = `${API.adsStreak}?userId=${encodeURIComponent(userId)}`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error || res.statusText);
      setStreakData(normalizeStreakPayload(data) ?? data);
    } catch (e) {
      setErrStreak(e.message || 'Failed to load streak');
      setStreakData(null);
    } finally {
      setLoadingStreak(false);
    }
  }, [userId, loggedInUserId, isViewingOwnActivity]);

  const fetchDreameyDiamonds = useCallback(async (opts) => {
    const forceNetwork = opts?.forceNetwork === true;
    if (!userId) return;
    if (!forceNetwork) {
      const raw = getOwnDashboardRawBundle(userId, loggedInUserId, isViewingOwnActivity);
      if (raw?.coins?.success) {
        const data = raw.coins;
        const apiIds = parseClaimedRankRewardIdsForDreameyVids(data.apps);
        setClaimedRankRewardIds(apiIds);
        writeStoredRankClaims(userId, apiIds);
        setDreameyDiamondsTotal(dreameyDiamondsDisplayTotal(data, userId));
        return;
      }
      const cachedSelfInfo = getOwnDashboardCacheBundle(userId, loggedInUserId, isViewingOwnActivity)?.selfInfo;
      if (Number.isFinite(Number(cachedSelfInfo?.totalDreameyDiamonds))) {
        setDreameyDiamondsTotal(Number(cachedSelfInfo.totalDreameyDiamonds));
        return;
      }
      if (isViewingOwnActivity) {
        setDreameyDiamondsTotal(0);
        return;
      }
    }
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
      const res = await fetch(API.appCoinsApps, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success || !Array.isArray(data.apps)) {
        setDreameyDiamondsTotal(0);
        return;
      }
      const apiIds = parseClaimedRankRewardIdsForDreameyVids(data.apps);
      setClaimedRankRewardIds(apiIds);
      writeStoredRankClaims(userId, apiIds);
      const total = dreameyDiamondsDisplayTotal(data, userId);
      setDreameyDiamondsTotal(total);
      mergeDashboardRawFields(userId, { coins: data });
      mergeHomeCacheSelfInfo(userId, { totalDreameyDiamonds: total });
    } catch {
      setDreameyDiamondsTotal(0);
    }
  }, [userId, loggedInUserId, isViewingOwnActivity]);

  const fetchDownlineStreakRewards = useCallback(async () => {
    if (!isViewingOwnActivity || !userId) {
      setDownlineCycleState([]);
      setClaimedDownlineStreakKeys([]);
      setErrDownlineStreak('');
      return;
    }
    const localKeys = readStoredDownlineClaimKeys(userId);
    setClaimedDownlineStreakKeys(localKeys);
    setLoadingDownlineStreak(true);
    setErrDownlineStreak('');
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
      const url = `${API.appCoinsDownlineStreak}?appId=${encodeURIComponent(DREAMEY_VIDS_APP_ID)}`;
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data.error || data.message || 'Could not load downline rewards.');
      }
      const claims = Array.isArray(data.claims) ? data.claims : [];
      const serverKeys = claims.map((c) =>
        downlineDreameyClaimKey(c.referredUserId, c.milestoneDays, c.rewardCycle)
      );
      const stored = readStoredDownlineClaims(userId);
      const byKey = new Map(stored.map((row) => [row.key, row]));
      claims.forEach((c) => {
        const key = downlineDreameyClaimKey(c.referredUserId, c.milestoneDays, c.rewardCycle);
        if (!byKey.has(key)) {
          const coins =
            Number(c.diamondAmount) ||
            downlineCoinsForMilestoneDays(c.milestoneDays);
          byKey.set(key, { key, coins });
        }
      });
      writeStoredDownlineClaims(userId, Array.from(byKey.values()));
      setClaimedDownlineStreakKeys(mergeUniqueStrings(localKeys, serverKeys));
      setDownlineCycleState(Array.isArray(data.cycleState) ? data.cycleState : []);
    } catch (e) {
      setErrDownlineStreak(e.message || 'Failed to load downline rewards.');
      setClaimedDownlineStreakKeys(localKeys);
      setDownlineCycleState([]);
    } finally {
      setLoadingDownlineStreak(false);
    }
  }, [isViewingOwnActivity, userId]);

  const fetchMyProfile = useCallback(async () => {
    if (!userId) return;
    const raw = getOwnDashboardRawBundle(userId, loggedInUserId, isViewingOwnActivity);
    if (raw?.profile) {
      const data = raw.profile;
      const u = data?.user ?? data ?? null;
      setMyProfileUser(u && typeof u === 'object' ? u : null);
      return;
    }
    const cachedSelfInfo = getOwnDashboardCacheBundle(userId, loggedInUserId, isViewingOwnActivity)?.selfInfo;
    if (isViewingOwnActivity && cachedSelfInfo) {
      setMyProfileUser({
        UserID: userId,
        FirstName: cachedSelfInfo.name,
        ReferralCode: String(cachedSelfInfo.referralLink ?? '').split('/').pop() || '',
      });
      return;
    }
    try {
      const cachedProfile = readUserSessionProfileCache(userId)?.profile;
      if (cachedProfile) {
        const u = cachedProfile?.user ?? cachedProfile ?? null;
        setMyProfileUser(u && typeof u === 'object' ? u : null);
        return;
      }
      if (isViewingOwnActivity) {
        setMyProfileUser(null);
        return;
      }
      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
      const res = await fetch(API.userProfile(userId), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      if (!res.ok) {
        setMyProfileUser(null);
        return;
      }
      writeUserSessionProfileCache(userId, data);
      const u = data?.user ?? data ?? null;
      setMyProfileUser(u && typeof u === 'object' ? u : null);
    } catch {
      setMyProfileUser(null);
    }
  }, [userId, loggedInUserId, isViewingOwnActivity]);

  const fetchPlacementTreeDepth = useCallback(async () => {
    if (!userId) return;
    const raw = getOwnDashboardRawBundle(userId, loggedInUserId, isViewingOwnActivity);
    if (raw?.placementMaxDepth != null || raw?.rankSummary?.rankQualifyingLevel != null) {
      setPlacementMaxDepth(Number(raw.placementMaxDepth ?? raw.rankSummary.rankQualifyingLevel) || 0);
      setLoadingPlacementDepth(false);
      setErrPlacementDepth('');
      return;
    }
    if (isViewingOwnActivity) {
      setPlacementMaxDepth(0);
      setLoadingPlacementDepth(false);
      setErrPlacementDepth('');
      return;
    }
    const code = String(
      myProfileUser?.ReferralCode ??
        myProfileUser?.referralCode ??
        jwtPayload?.referralCode ??
        jwtPayload?.ReferralCode ??
        ''
    ).trim();
    if (!code) {
      setPlacementMaxDepth(0);
      setErrPlacementDepth('');
      return;
    }
    setLoadingPlacementDepth(true);
    setErrPlacementDepth('');
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
      const url = API.directReferrals(encodeURIComponent(code));
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || data.message || res.statusText || 'Failed to load referral tree');
      }
      setPlacementMaxDepth(maxPlacementDepthFromDirectReferralsResponse(data));
    } catch (e) {
      setErrPlacementDepth(e.message || 'Tree unavailable');
      /* Keep last successful depth so ranks don’t drop to 0 on transient errors. */
    } finally {
      setLoadingPlacementDepth(false);
    }
  }, [userId, myProfileUser, jwtPayload, loggedInUserId, isViewingOwnActivity]);

  const fetchReferralsProgress = useCallback(async () => {
    if (!userId) return;
    const raw = getOwnDashboardRawBundle(userId, loggedInUserId, isViewingOwnActivity);
    if (raw?.referralsActivity) {
      const a = raw.referralsActivity;
      const homeCache = getOwnDashboardCacheBundle(userId, loggedInUserId, isViewingOwnActivity);
      const homeReferralRows = Array.isArray(homeCache?.referrals) ? homeCache.referrals : [];
      setDirectReferrals(Array.isArray(a.directReferrals) ? a.directReferrals : []);
      setReferralLevelByUserId(
        a.referralLevelByUserId && typeof a.referralLevelByUserId === 'object'
          ? a.referralLevelByUserId
          : {}
      );
      setReferralDesignationByUserId(
        a.referralDesignationByUserId && typeof a.referralDesignationByUserId === 'object'
          ? a.referralDesignationByUserId
          : {}
      );
      setReferralsProgress(
        mergeReferralProgressWithHomeStreakRows(a.referralsProgress ?? null, homeReferralRows)
      );
      setLoadingReferrals(false);
      setErrReferrals('');
      return;
    }
    if (isViewingOwnActivity) {
      const homeCache = getOwnDashboardCacheBundle(userId, loggedInUserId, isViewingOwnActivity);
      const directList = Array.isArray(raw?.refs?.referrals) ? raw.refs.referrals : [];
      const homeReferralRows = Array.isArray(homeCache?.referrals) ? homeCache.referrals : [];
      const levelMap = {};
      const designationMap = {};
      if (raw?.levels?.success && Array.isArray(raw.levels.users)) {
        raw.levels.users.forEach((u) => {
          const id = Number(u?.UserID ?? u?.userId);
          if (!Number.isFinite(id)) return;
          levelMap[id] = Number(u?.Level ?? u?.level ?? 0) || 0;
          const dn = String(u?.DesignationName ?? u?.designationName ?? '').trim();
          if (dn && dn.toLowerCase() !== 'null') designationMap[id] = dn;
        });
      }
      const progressRows = homeReferralRows.map((row) => ({
        userId: Number(row.userId),
        adsWatchedToday: Number(row.adsWatchedToday ?? 0) || 0,
        currentStreak: Number(row.currentStreak ?? 0) || 0,
        longestStreak: Number(row.longestStreak ?? row.currentStreak ?? 0) || 0,
        goalPerDay: Number(row.goalPerDay ?? DAILY_TARGET) || DAILY_TARGET,
      })).filter((row) => Number.isFinite(row.userId));
      setDirectReferrals(directList);
      setReferralLevelByUserId(levelMap);
      setReferralDesignationByUserId(designationMap);
      setReferralsProgress({ success: true, referrals: progressRows });
      setLoadingReferrals(false);
      setErrReferrals('');
      return;
    }
    setLoadingReferrals(true);
    setErrReferrals('');
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [listRes, streakRes, levelRes] = await Promise.all([
        fetch(API.originalReferrals(userId), { headers }),
        fetch(API.directReferralsStreakCompleters, { headers }),
        fetch(API.getUsersByLevel, { headers }),
      ]);

      const listData = await listRes.json();
      const streakData = await streakRes.json();
      const levelData = await levelRes.json();

      if (!listRes.ok) {
        throw new Error(listData.error || listData.message || listRes.statusText);
      }
      if (!streakRes.ok) {
        throw new Error(streakData.error || streakData.message || streakRes.statusText);
      }

      const levelMap = {};
      const designationMap = {};
      if (levelRes.ok && levelData?.success && Array.isArray(levelData.users)) {
        levelData.users.forEach((u) => {
          if (u?.UserID == null) return;
          const id = Number(u.UserID);
          levelMap[id] = u.Level;
          const dn = String(u?.DesignationName ?? u?.designationName ?? '').trim();
          if (dn && dn.toLowerCase() !== 'null') designationMap[id] = dn;
        });
      }
      setReferralLevelByUserId(levelMap);
      setReferralDesignationByUserId(designationMap);

      const directList = Array.isArray(listData.referrals) ? listData.referrals : [];
      setDirectReferrals(directList);

      const rawCompleters = streakData.referrals ?? streakData.Referrals ?? [];
      const completerList = Array.isArray(rawCompleters) ? rawCompleters : [];
      const byRefId = new Map();
      for (const row of completerList) {
        const n = normalizeReferralProgressFromApiRow(row);
        if (n) byRefId.set(n.userId, n);
      }

      /* streak-completers often only includes members with long streaks; fetch full /ads/streak for others. */
      const missingIds = directList
        .map((ref) => Number(ref.UserID))
        .filter((id) => Number.isFinite(id) && !byRefId.has(id));

      await Promise.all(
        missingIds.map(async (refUserId) => {
          try {
            const url = `${API.adsStreak}?userId=${encodeURIComponent(refUserId)}`;
            const res = await fetch(url, { headers });
            const data = await res.json();
            if (!res.ok) return;
            const norm = normalizeStreakPayload(data);
            if (!norm) return;
            byRefId.set(refUserId, {
              userId: refUserId,
              adsWatchedToday: norm.adsWatchedToday,
              currentStreak: norm.currentStreak,
              longestStreak: norm.longestStreak,
              goalPerDay: norm.goalPerDay,
            });
          } catch {
            /* ignore per-referral errors */
          }
        })
      );

      const mergedReferrals = directList.map((ref) => {
        const id = Number(ref.UserID);
        if (!Number.isFinite(id)) return null;
        return (
          byRefId.get(id) ?? {
            userId: id,
            adsWatchedToday: 0,
            currentStreak: 0,
            longestStreak: 0,
            goalPerDay: DAILY_TARGET,
          }
        );
      }).filter(Boolean);

      setReferralsProgress({ ...streakData, referrals: mergedReferrals });
    } catch (e) {
      setErrReferrals(e.message || 'Failed to load referrals');
      setDirectReferrals([]);
      setReferralsProgress(null);
      setReferralLevelByUserId({});
      setReferralDesignationByUserId({});
    } finally {
      setLoadingReferrals(false);
    }
  }, [userId, loggedInUserId, isViewingOwnActivity]);

  useEffect(() => {
    fetchStreak();
  }, [fetchStreak]);

  useEffect(() => {
    if (userId) fetchMyProfile();
  }, [userId, fetchMyProfile]);

  useEffect(() => {
    if (userId) void fetchDreameyDiamonds();
  }, [userId, fetchDreameyDiamonds]);

  useEffect(() => {
    if (userId) fetchReferralsProgress();
  }, [userId, fetchReferralsProgress]);

  useEffect(() => {
    if (userId) void fetchPlacementTreeDepth();
  }, [userId, fetchPlacementTreeDepth]);

  useEffect(() => {
    setClaimedStreakRewardIds(readStoredStreakClaims(userId));
  }, [userId]);

  useEffect(() => {
    void fetchDownlineStreakRewards();
  }, [fetchDownlineStreakRewards]);

  useEffect(() => {
    if (!location.state?.openDownlineRewards) return;
    setProgressInfoDialogSection('downline');
    setStreakRewardsDialogOpen(true);
    const returnPath = location.state?.returnPath;
    streakRewardsReturnPathRef.current =
      typeof returnPath === 'string' && returnPath.startsWith('/dashboard/') ? returnPath : null;
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state?.openDownlineRewards, location.pathname, navigate]);

  const closeStreakRewardsDialog = useCallback(() => {
    setStreakRewardsDialogOpen(false);
    const returnPath = streakRewardsReturnPathRef.current;
    if (returnPath) {
      streakRewardsReturnPathRef.current = null;
      navigate(returnPath, { replace: true });
    }
  }, [navigate]);

  const handleClaimStreakReward = useCallback(
    (rewardId, rewardTitle) => {
      if (!userId || !rewardId) return;
      setClaimedStreakRewardIds((prev) => {
        if (prev.includes(rewardId)) return prev;
        if (prev.length > 0) return prev; // one prize choice per milestone (pool is "win one of these")
        const next = [...prev, rewardId];
        writeStoredStreakClaims(userId, next);
        setClaimSnackbar({
          open: true,
          message: `Recorded: ${rewardTitle}. We will verify your ${STREAK_MILESTONE_DAYS} streak.`,
        });
        return next;
      });
    },
    [userId]
  );

  const handleClaimRankReward = useCallback(
    async (rewardId, rankLabel, bonusLabel) => {
      if (!userId || !rewardId) return;
      setRankClaimInFlight(true);
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
        const res = await fetch(API.appCoinsClaimRank, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ appId: DREAMEY_VIDS_APP_ID, rewardId }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.success) {
          throw new Error(data.error || data.message || 'Could not save claim.');
        }
        const ids = Array.isArray(data.claimedRankRewardIds) ? data.claimedRankRewardIds : [];
        setClaimedRankRewardIds(ids);
        writeStoredRankClaims(userId, ids);
        markUserDashboardHomeRequiresFreshFetch();
        try {
          await Promise.all([fetchMyProfile(), fetchReferralsProgress()]);
        } finally {
          clearUserDashboardHomeForceNetwork();
        }
        await fetchDreameyDiamonds({ forceNetwork: true });
        setClaimSnackbar({
          open: true,
          message: data.alreadyClaimed
            ? `${rankLabel}: already claimed.`
            : `${rankLabel} — ${bonusLabel} added to your Dreamey Diamonds.`,
        });
      } catch (e) {
        setClaimSnackbar({
          open: true,
          message: e.message || 'Could not save claim. Try again.',
        });
      } finally {
        setRankClaimInFlight(false);
      }
    },
    [userId, fetchDreameyDiamonds, fetchMyProfile, fetchReferralsProgress]
  );

  const handleClaimDownlineDreamey = useCallback(
    async (refUserId, milestoneDays, coins, refDisplayName, rewardCycle = 1) => {
      if (!userId || refUserId == null || !isViewingOwnActivity) return;
      const milestoneCoins = Number(coins) || downlineCoinsForMilestoneDays(milestoneDays);
      const ck = downlineDreameyClaimKey(refUserId, milestoneDays, rewardCycle);
      if (claimedDownlineStreakKeys.includes(ck)) {
        setClaimSnackbar({
          open: true,
          message: `${refDisplayName}: ${milestoneDays} Days already claimed for this cycle.`,
        });
        return;
      }
      setDownlineClaimInFlight(true);
      try {
        appendStoredDownlineClaim(userId, { key: ck, coins: milestoneCoins, milestoneDays });
        const nextKeys = mergeUniqueStrings(claimedDownlineStreakKeys, [ck]);
        setClaimedDownlineStreakKeys(nextKeys);
        setDreameyDiamondsTotal((prev) => {
          const next = (Number(prev) || 0) + milestoneCoins;
          mergeHomeCacheSelfInfo(userId, { totalDreameyDiamonds: next });
          return next;
        });

        const token = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
        try {
          const res = await fetch(API.appCoinsClaimDownlineStreak, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              appId: DREAMEY_VIDS_APP_ID,
              referredUserId: refUserId,
              milestoneDays,
            }),
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok && data?.success) {
            await fetchDownlineStreakRewards();
          }
        } catch {
          /* local claim already applied */
        }

        setClaimSnackbar({
          open: true,
          message: `${milestoneCoins} 💎 added for ${refDisplayName} (${milestoneDays} Days streak).`,
        });
      } catch (e) {
        setClaimSnackbar({
          open: true,
          message: e.message || 'Could not claim. Try again.',
        });
      } finally {
        setDownlineClaimInFlight(false);
      }
    },
    [userId, isViewingOwnActivity, claimedDownlineStreakKeys, fetchDownlineStreakRewards]
  );

  /** From profile/JWT/users-by-level map (often empty for self — not sufficient alone). */
  const legacyProgramLevel = useMemo(
    () => resolveUserProgramLevel(myProfileUser, jwtPayload, referralLevelByUserId, userId),
    [myProfileUser, jwtPayload, referralLevelByUserId, userId]
  );

  /**
   * Ranks & Diamonds / DB designation: max of API “Level” fields and measured downline depth
   * (GET /referrals/direct/:code — placement tree, same as Community Network).
   * Direct-referral count for ranks/Diamonds uses GET /referrals/original/:userId (signup / OriginalReferredByCode).
   */
  const rankQualifyingLevel = useMemo(() => {
    if (placementMaxDepth == null) return legacyProgramLevel;
    return Math.max(legacyProgramLevel, placementMaxDepth);
  }, [legacyProgramLevel, placementMaxDepth]);

  const rankProfileComplete = useMemo(() => isProfileCompleteForRanks(myProfileUser), [myProfileUser]);

  /** Referrals loaded successfully — not loading, no error, and we have a merged progress payload. */
  const referralsDataReady = !loadingReferrals && referralsProgress != null && !errReferrals;

  const downlineReferralsByStreakDesc = useMemo(() => {
    const progressList = Array.isArray(referralsProgress?.referrals) ? referralsProgress.referrals : [];
    const progressByUserId = new Map(progressList.map((row) => [Number(row.userId), row]));
    return [...directReferrals].sort((refA, refB) => {
      const progressA = progressByUserId.get(Number(refA.UserID));
      const progressB = progressByUserId.get(Number(refB.UserID));
      const curA = referralCurrentStreakFromProgress(progressA);
      const curB = referralCurrentStreakFromProgress(progressB);
      if (curA !== curB) return curB - curA;
      const bestA = bestReferralStreakFromProgress(progressA);
      const bestB = bestReferralStreakFromProgress(progressB);
      if (bestA !== bestB) return bestB - bestA;
      const nameA =
        [refA.FirstName, refA.LastName].filter(Boolean).join(' ') || refA.UserName || '';
      const nameB =
        [refB.FirstName, refB.LastName].filter(Boolean).join(' ') || refB.UserName || '';
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });
  }, [directReferrals, referralsProgress]);

  const diamondCapTotal = useMemo(() => totalRankLadderDiamondsCap(), []);

  const diamondQualifiedTotal = useMemo(
    () =>
      qualifiedRankDiamondsTotal(
        rankQualifyingLevel,
        directReferrals.length,
        rankProfileComplete,
        referralsDataReady
      ),
    [rankQualifyingLevel, directReferrals.length, rankProfileComplete, referralsDataReady]
  );

  const qualifiedRankTierCount = useMemo(
    () =>
      countQualifiedRankTiers(
        rankQualifyingLevel,
        directReferrals.length,
        rankProfileComplete,
        referralsDataReady
      ),
    [rankQualifyingLevel, directReferrals.length, rankProfileComplete, referralsDataReady]
  );

  /** Same tier title as Ranks & Diamonds — avoids stale API DesignationName (e.g. Team Leader vs Silver Executive). */
  const ladderRankTitleForChips = useMemo(
    () =>
      highestQualifiedRankNameForDisplay(
        rankQualifyingLevel,
        directReferrals.length,
        rankProfileComplete,
        referralsDataReady
      ),
    [rankQualifyingLevel, directReferrals.length, rankProfileComplete, referralsDataReady]
  );

  const myRankChips = useMemo(
    () =>
      getActivityRankChipsForSelf(
        myProfileUser,
        jwtPayload,
        referralDesignationByUserId,
        userId,
        /* No fake “Leader” when DesignationID is null — ladder title or Member only. */
        false,
        ladderRankTitleForChips
      ),
    [
      myProfileUser,
      jwtPayload,
      referralDesignationByUserId,
      userId,
      directReferrals.length,
      ladderRankTitleForChips,
    ]
  );

  /** My Activity only: hide role chips that duplicate Home / clutter the header (Users Activity unchanged). */
  const headerRankChips = useMemo(() => {
    if (!showMyActivity) return myRankChips;
    return myRankChips.filter((label) => label !== 'Admin' && label !== 'Leader');
  }, [showMyActivity, myRankChips]);

  const refreshActivityHeader = useCallback(() => {
    void fetchStreak();
    void fetchReferralsProgress();
    void fetchPlacementTreeDepth();
    void fetchMyProfile();
    void fetchDreameyDiamonds({ forceNetwork: true });
    void fetchDownlineStreakRewards();
  }, [
    fetchStreak,
    fetchReferralsProgress,
    fetchPlacementTreeDepth,
    fetchMyProfile,
    fetchDreameyDiamonds,
    fetchDownlineStreakRewards,
  ]);

  const activityDisplayName = useMemo(() => {
    const u = myProfileUser;
    const first = String(u?.FirstName ?? u?.firstName ?? jwtPayload?.firstName ?? '').trim();
    const last = String(u?.LastName ?? u?.lastName ?? jwtPayload?.lastName ?? '').trim();
    const full = [first, last].filter(Boolean).join(' ');
    if (full) return full;
    const un = String(u?.UserName ?? u?.userName ?? jwtPayload?.userName ?? '').trim();
    if (un) return un;
    return 'Member';
  }, [myProfileUser, jwtPayload]);

  const streakStats = useMemo(() => {
    if (!streakData || loadingStreak || errStreak) return null;
    const todayCount = Math.max(0, streakData.adsWatchedToday ?? 0);
    const goal = Math.max(1, streakData.goalPerDay ?? DAILY_TARGET);
    const streakDays = Math.max(0, streakData.currentStreak ?? 0);
    const bestStreakDays = bestStreakFromData(streakData);
    const pct = goal > 0 ? Math.min(100, (todayCount / goal) * 100) : 0;
    return { todayCount, goal, streakDays, bestStreakDays, pct };
  }, [streakData, loadingStreak, errStreak]);

  if (!userId) {
    return (
      <Box sx={{ p: 3, maxWidth: 640, mx: 'auto' }}>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          Please sign in to view your activity.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', py: { xs: 1, sm: 2 }, px: { xs: 0.5, sm: 1 } }}>
        <Stack spacing={0.75} sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                background: portal.gradientHero,
                boxShadow: `0 8px 32px ${alpha(portal.cyan, 0.35)}`,
              }}
            >
              <TodayIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={portalGradientTextSx({
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                })}
              >
                {view === 'users' ? 'Users Activity' : 'My Activity'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, maxWidth: 680 }}>
                {view === 'users'
                  ? 'Today, streak, and best run per person.'
                  : 'Complete activity to earn rewards and diamonds.'}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        {showMyActivity && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: alpha(theme.palette.divider, 0.9),
            background: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.55 : 0.92),
            backdropFilter: 'blur(12px)',
            boxShadow:
              theme.palette.mode === 'dark'
                ? `0 8px 40px ${alpha('#000', 0.35)}, inset 0 1px 0 ${alpha('#fff', 0.06)}`
                : `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, transparent 60%)`,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1.5}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.25}
                flexWrap="wrap"
                useFlexGap
                sx={{ flex: 1, minWidth: 0 }}
              >
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                  <LocalFireDepartmentIcon sx={{ color: 'primary.main', fontSize: 26, flexShrink: 0 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: { xs: 'normal', sm: 'nowrap' },
                      maxWidth: { xs: '100%', sm: 280 },
                    }}
                  >
                    {activityDisplayName}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {headerRankChips.map((label, idx) => (
                    <Chip
                      key={`${label}-${idx}`}
                      label={label}
                      size="small"
                      variant="outlined"
                      sx={rankChipSx(label, theme)}
                    />
                  ))}
                  {placementMaxDepth !== null && (
                    <Tooltip title="Max downline depth from your referral tree — same as Community Network (Network Lv)">
                      <Chip
                        key="network-depth"
                        label={`Lv ${placementMaxDepth}`}
                        size="small"
                        variant="outlined"
                        sx={rankChipSx('Network', theme)}
                      />
                    </Tooltip>
                  )}
                  {loadingPlacementDepth && (
                    <Chip
                      size="small"
                      label="Loading network…"
                      variant="outlined"
                      sx={{ fontWeight: 600, opacity: 0.85 }}
                    />
                  )}
                  {errPlacementDepth && !loadingPlacementDepth && (
                    <Tooltip title={errPlacementDepth}>
                      <Chip size="small" label="Network depth unavailable" color="warning" variant="outlined" />
                    </Tooltip>
                  )}
                  {showMyActivity && (
                    <>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: alpha(theme.palette.divider, 0.85),
                          borderRadius: 2,
                          px: 1.25,
                          py: 0.6,
                          background:
                            theme.palette.mode === 'dark'
                              ? `linear-gradient(145deg, ${alpha('#1a1f2e', 0.9)} 0%, ${alpha('#10131d', 0.9)} 100%)`
                              : alpha(theme.palette.background.paper, 0.95),
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 800, color: 'common.white' }}>
                          {dreameyDiamondsTotal === null
                            ? '…'
                            : Number(dreameyDiamondsTotal).toLocaleString()}{' '}
                          💎
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor:
                            streakStats && streakStats.todayCount < streakStats.goal
                              ? alpha(theme.palette.warning.main, 0.55)
                              : alpha(theme.palette.divider, 0.85),
                          borderRadius: 2,
                          px: 1.25,
                          py: 0.6,
                          background:
                            theme.palette.mode === 'dark'
                              ? `linear-gradient(145deg, ${alpha('#1a1f2e', 0.9)} 0%, ${alpha('#10131d', 0.9)} 100%)`
                              : alpha(theme.palette.background.paper, 0.95),
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography variant="body2" sx={{ fontWeight: 800, color: 'common.white' }}>
                            {loadingStreak && !streakStats ? '…' : streakStats?.streakDays ?? 0} 🔥
                          </Typography>
                          {streakStats && streakStats.todayCount < streakStats.goal && (
                            <>
                              <Tooltip title="Today's ad goal not completed yet — finish before this UTC timer hits 00:00:00 to keep your streak safe.">
                                <WarningAmberIcon
                                  sx={{ fontSize: 22, color: 'warning.main' }}
                                  aria-label="Today's streak goal not met"
                                />
                              </Tooltip>
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  fontFamily: 'ui-monospace, monospace',
                                  fontSize: '0.7rem',
                                  color: 'warning.light',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {formatUtcMidnightCountdown(utcMidnightRemainingMs)} UTC
                              </Typography>
                            </>
                          )}
                        </Stack>
                      </Box>
                    </>
                  )}
                </Stack>
              </Stack>
              <IconButton
                onClick={refreshActivityHeader}
                disabled={loadingStreak}
                aria-label="Refresh activity, referrals, and network depth"
                sx={{
                  ...(SHOW_ACTIVITY_PROFILE_REFRESH_BUTTON
                    ? {}
                    : {
                        display: 'none',
                      }),
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: alpha(theme.palette.action.hover, 0.5),
                  flexShrink: 0,
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12) },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Stack>
          </Box>

          <Box sx={{ p: 2.5 }}>
            <Stack spacing={2.5}>
            {loadingStreak && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <CircularProgress size={22} />
                <Typography variant="body2" color="text.secondary">
                  Loading your stats…
                </Typography>
              </Stack>
            )}
            {errStreak && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {errStreak}
              </Alert>
            )}
            {!loadingStreak && !errStreak && (
              <RanksDiamondsSegmentedMilestoneTrack
                theme={theme}
                ariaLabel={`Rank ladder: network depth ${rankQualifyingLevel} (levels) and ${directReferrals.length} direct referrals. A tier counts when both meet its thresholds. ${qualifiedRankTierCount} of ${RANK_DIAMOND_TIER_TOTAL} tiers fully qualified; ${diamondQualifiedTotal} of ${diamondCapTotal} Diamonds. Segments: Leader through Diamond Executive.`}
                outerBg={alpha(portal.purple, theme.palette.mode === 'dark' ? 0.1 : 0.06)}
                levelsBarBackground={`linear-gradient(90deg, ${alpha(theme.palette.warning.main, 0.95)} 0%, ${portal.purple} 45%, ${portal.pink} 100%)`}
                referralsBarBackground={`linear-gradient(90deg, ${alpha(theme.palette.info.light, 0.85)} 0%, ${theme.palette.secondary.main} 55%, ${alpha(portal.cyan, 0.9)} 100%)`}
                claimedRankRewardIds={claimedRankRewardIds}
                onClaimRank={handleClaimRankReward}
                rankClaimInFlight={rankClaimInFlight}
                userProgramLevel={rankQualifyingLevel}
                directRefCount={directReferrals.length}
                rankProfileComplete={rankProfileComplete}
                referralsDataReady={referralsDataReady}
                referralsFetchError={errReferrals}
              />
            )}
            {!loadingStreak && !errStreak && streakStats && (
              <ProgressMilestoneTrack
                theme={theme}
                title="STREAK"
                ariaLabel={`Streak progress: current ${streakStats.streakDays}, best ${streakStats.bestStreakDays}; milestone 0 to ${STREAK_MILESTONE_DAYS}`}
                fillPercent={(streakStats.streakDays / STREAK_MILESTONE_DAYS) * 100}
                centerLabel={
                  streakStats.streakDays >= STREAK_MILESTONE_DAYS
                    ? 'Completed'
                    : `${streakStats.streakDays}/${STREAK_MILESTONE_DAYS}`
                }
                outerBg={alpha(portal.amber, theme.palette.mode === 'dark' ? 0.08 : 0.05)}
                centerLabelColor={
                  streakStats.streakDays >= STREAK_MILESTONE_DAYS ? theme.palette.success.main : portal.amber
                }
                barBackground={portal.gradientBar}
                titleEndAdornment={
                  <Tooltip title={`${STREAK_MILESTONE_DAYS} prizes — tap for details`}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setProgressInfoDialogSection('streak');
                        setStreakRewardsDialogOpen(true);
                      }}
                      aria-label="Open streak prize rewards"
                      sx={{
                        color: 'text.secondary',
                        p: 0.375,
                        '&:hover': { color: portal.amber, bgcolor: alpha(portal.amber, 0.1) },
                      }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                }
              />
            )}
            </Stack>
          </Box>
        </Paper>
        )}

        {showUsersActivity && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: alpha(theme.palette.divider, 0.9),
            background: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.55 : 0.92),
            backdropFilter: 'blur(12px)',
            boxShadow:
              theme.palette.mode === 'dark'
                ? `0 8px 40px ${alpha('#000', 0.3)}`
                : `0 8px 28px ${alpha(portal.purple, 0.08)}`,
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              background: `linear-gradient(90deg, ${alpha(portal.purple, 0.1)} 0%, transparent 55%)`,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: alpha(portal.purple, 0.2),
                    color: portal.pink,
                  }}
                >
                  <GroupIcon />
                </Avatar>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Direct referrals
                    </Typography>
                    <Tooltip title="Dreamey Diamonds when your referrals hit streak milestones (2–10 members)">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setProgressInfoDialogSection('downline');
                          setStreakRewardsDialogOpen(true);
                        }}
                        aria-label="Downline streak Dreamey Diamond rewards"
                        sx={{
                          color: 'text.secondary',
                          '&:hover': {
                            color: portal.pink,
                            bgcolor: alpha(portal.purple, 0.12),
                          },
                        }}
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Today, streak, and best run per person
                  </Typography>
                </Box>
              </Stack>
              <IconButton
                onClick={fetchReferralsProgress}
                disabled={loadingReferrals}
                aria-label="Refresh referrals"
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: alpha(theme.palette.action.hover, 0.5),
                  '&:hover': { bgcolor: alpha(portal.purple, 0.15) },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Stack>
          </Box>

          <Box sx={{ p: 2.5 }}>
            {loadingReferrals && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <CircularProgress size={22} />
                <Typography variant="body2" color="text.secondary">
                  Loading your network…
                </Typography>
              </Stack>
            )}
            {errReferrals && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {errReferrals}
              </Alert>
            )}
            {!loadingReferrals && !errReferrals && referralsProgress != null && (
              <>
                {directReferrals.length > 0 ? (
                  <Stack spacing={2}>
                    {directReferrals.map((ref) => {
                      const progressMatch = (referralsProgress.referrals || []).find(
                        (r) => Number(r.userId) === Number(ref.UserID)
                      );
                      const today = progressMatch?.adsWatchedToday ?? 0;
                      const current = progressMatch?.currentStreak ?? 0;
                      const longest = progressMatch?.longestStreak ?? 0;
                      const refGoal = Math.max(1, progressMatch?.goalPerDay ?? DAILY_TARGET);
                      const refRankChips = getActivityRankChipsForReferral(
                        ref,
                        progressMatch,
                        referralDesignationByUserId
                      );
                      const displayName =
                        [ref.FirstName, ref.LastName].filter(Boolean).join(' ') ||
                        ref.UserName ||
                        `User #${ref.UserID}`;
                      return (
                        <Paper
                          key={ref.UserID || ref.ReferralID}
                          elevation={0}
                          sx={{
                            p: 0,
                            borderRadius: 2.5,
                            overflow: 'hidden',
                            border: '1px solid',
                            borderColor: alpha(theme.palette.divider, 0.8),
                            background: `linear-gradient(105deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(theme.palette.background.paper, 0.3)} 18%, ${alpha(theme.palette.background.paper, 0.15)} 100%)`,
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              borderColor: alpha(theme.palette.primary.main, 0.45),
                              boxShadow: `0 12px 36px ${alpha(theme.palette.common.black, 0.2)}`,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              width: '100%',
                              minHeight: 4,
                              background: portal.gradientBar,
                            }}
                          />
                          <Box sx={{ p: 2.25 }}>
                            <Stack
                              direction={{ xs: 'column', md: 'row' }}
                              spacing={2}
                              justifyContent="space-between"
                              alignItems={{ xs: 'flex-start', md: 'center' }}
                            >
                              <Box sx={{ minWidth: 0, width: '100%' }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                    mb: 0.5,
                                    width: '100%',
                                  }}
                                >
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      fontWeight: 700,
                                      minWidth: 0,
                                      flex: '1 1 auto',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      pr: 0.5,
                                    }}
                                  >
                                    {displayName}
                                  </Typography>
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    flexWrap="wrap"
                                    useFlexGap
                                    spacing={0.75}
                                    justifyContent="flex-end"
                                    sx={{ flexShrink: 0 }}
                                  >
                                    {refRankChips.map((label, idx) => (
                                      <Chip
                                        key={`${ref.UserID}-${label}-${idx}`}
                                        label={label}
                                        size="small"
                                        variant="outlined"
                                        sx={rankChipSx(label, theme)}
                                      />
                                    ))}
                                  </Stack>
                                </Box>
                                {ref.Email && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', wordBreak: 'break-all' }}
                                  >
                                    {ref.Email}
                                  </Typography>
                                )}
                              </Box>
                              <Stack
                                direction="row"
                                spacing={{ xs: 0.75, sm: 1.25 }}
                                useFlexGap
                                sx={{
                                  flexShrink: 0,
                                  width: { xs: '100%', md: 'auto' },
                                  alignSelf: { xs: 'stretch', md: 'auto' },
                                }}
                              >
                                {[
                                  { label: 'Today', value: `${today} / ${refGoal}`, tone: portal.cyan },
                                  { label: 'Streak', value: `${current} d`, tone: portal.amber },
                                  { label: 'Best', value: `${longest} d`, tone: portal.statBest },
                                ].map((stat) => (
                                  <Box
                                    key={stat.label}
                                    sx={{
                                      flex: '1 1 0',
                                      minWidth: { xs: 0, sm: 108 },
                                      px: { xs: 1, sm: 2 },
                                      py: { xs: 1, sm: 1.25 },
                                      borderRadius: 2,
                                      border: '1px solid',
                                      borderColor: alpha(stat.tone, 0.3),
                                      bgcolor: alpha(stat.tone, 0.08),
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: { xs: '0.65rem', sm: undefined },
                                        lineHeight: 1.2,
                                        display: 'block',
                                      }}
                                    >
                                      {stat.label}
                                    </Typography>
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: 800,
                                        mt: 0.25,
                                        color: stat.tone,
                                        fontSize: { xs: '0.8rem', sm: undefined },
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {stat.value}
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            </Stack>
                          </Box>
                        </Paper>
                      );
                    })}
                    <Divider sx={{ borderStyle: 'dashed', borderColor: alpha(theme.palette.divider, 0.5) }} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                      {directReferrals.length} direct referral{directReferrals.length !== 1 ? 's' : ''} shown
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    You do not have direct referrals yet.
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Paper>
        )}

        <Dialog
          open={streakRewardsDialogOpen}
          onClose={closeStreakRewardsDialog}
          maxWidth="md"
          fullWidth
          scroll="paper"
          aria-labelledby="progress-rewards-dialog-title"
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: alpha(portal.purple, theme.palette.mode === 'dark' ? 0.35 : 0.2),
              bgcolor:
                theme.palette.mode === 'dark' ? portal.surfaceCard : theme.palette.background.paper,
              boxShadow:
                theme.palette.mode === 'dark'
                  ? `0 24px 80px ${alpha('#000', 0.55)}, 0 0 0 1px ${alpha(portal.cyan, 0.06)}`
                  : theme.shadows[8],
            },
          }}
          sx={{
            '& .MuiBackdrop-root': {
              bgcolor: alpha('#0B0B0F', 0.72),
              backdropFilter: 'blur(6px)',
            },
          }}
        >
          <DialogTitle
            id="progress-rewards-dialog-title"
            sx={{
              p: 0,
              fontWeight: 800,
              borderBottom: '1px solid',
              borderColor: alpha(theme.palette.divider, 0.5),
            }}
          >
            {progressInfoDialogSection === 'streak' ? (
              <Box
                sx={{
                  background: portal.gradientBold,
                  px: 2.5,
                  py: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background:
                      'radial-gradient(ellipse 80% 120% at 100% 0%, rgba(255,255,255,0.18) 0%, transparent 55%)',
                    pointerEvents: 'none',
                  },
                }}
              >
                <Stack direction="row" alignItems="center" gap={1.25} sx={{ position: 'relative', zIndex: 1 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: alpha('#fff', 0.12),
                      border: `1px solid ${alpha('#fff', 0.2)}`,
                    }}
                  >
                    <LocalFireDepartmentIcon sx={{ color: '#fff', fontSize: 26 }} />
                  </Box>
                  <Box>
                    <Typography
                      component="span"
                      variant="h6"
                      sx={{
                        color: '#fff',
                        fontWeight: 800,
                        letterSpacing: 0.3,
                        display: 'block',
                        lineHeight: 1.25,
                      }}
                    >
                      {STREAK_MILESTONE_DAYS} streak prizes
                    </Typography>
                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.85), fontWeight: 500 }}>
                      Win one of these · rules apply
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            ) : progressInfoDialogSection === 'downline' ? (
              <Box
                sx={{
                  background: portal.gradientBold,
                  px: 2.5,
                  py: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background:
                      'radial-gradient(ellipse 80% 120% at 100% 0%, rgba(255,255,255,0.18) 0%, transparent 55%)',
                    pointerEvents: 'none',
                  },
                }}
              >
                <Stack direction="row" alignItems="center" gap={1.25} sx={{ position: 'relative', zIndex: 1 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: alpha('#fff', 0.12),
                      border: `1px solid ${alpha('#fff', 0.2)}`,
                    }}
                  >
                    <GroupIcon sx={{ color: '#fff', fontSize: 26 }} />
                  </Box>
                  <Box>
                    <Typography
                      component="span"
                      variant="h6"
                      sx={{
                        color: '#fff',
                        fontWeight: 800,
                        letterSpacing: 0.3,
                        display: 'block',
                        lineHeight: 1.25,
                      }}
                    >
                      Downline streak rewards
                    </Typography>
                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.85), fontWeight: 500 }}>
                      Dreamey Diamonds when each referral hits a streak milestone
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            ) : (
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  background: `linear-gradient(90deg, ${alpha(theme.palette.warning.main, 0.12)} 0%, transparent 60%)`,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Ranks & Diamond bonuses
                </Typography>
              </Box>
            )}
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              pt: 2.5,
              borderColor: alpha(theme.palette.divider, 0.45),
              '&::-webkit-scrollbar': { width: 8 },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: 4,
                bgcolor: alpha(portal.purple, 0.35),
              },
            }}
          >
            {progressInfoDialogSection === 'ranks' && (
              <Stack spacing={2.5}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Ranks and Diamonds are the same path:</strong> each rank tier pays a{' '}
                  <strong>one-time Diamond bonus</strong> once your <strong>program level</strong>,{' '}
                  <strong>direct referrals</strong>, and <strong>profile</strong> meet that row below.
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap alignItems="center">
                  <Chip
                    size="small"
                    label={`Ranks depth: ${rankQualifyingLevel}${
                      placementMaxDepth != null ? ` (network ${placementMaxDepth})` : ''
                    }`}
                    sx={{ fontWeight: 700 }}
                  />
                  <Chip
                    size="small"
                    label={`Direct referrals: ${directReferrals.length}`}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 700 }}
                  />
                  <Chip
                    size="small"
                    label={rankProfileComplete ? 'Profile ready' : 'Profile incomplete'}
                    color={rankProfileComplete ? 'success' : 'warning'}
                    variant={rankProfileComplete ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 700 }}
                  />
                </Stack>
                {!rankProfileComplete && (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    <Typography variant="body2">
                      Add your first name, last name, and email in{' '}
                      <Link component={RouterLink} to="/dashboard/profile" fontWeight={700}>
                        Profile
                      </Link>{' '}
                      to unlock rank claims.
                    </Typography>
                  </Alert>
                )}
                {loadingReferrals && (
                  <Typography variant="caption" color="text.secondary">
                    Updating referral counts…
                  </Typography>
                )}
                <TableContainer sx={{ overflowX: 'auto', mx: { xs: -1, sm: 0 } }}>
                  <Table size="small" sx={{ minWidth: 520 }}>
                    <TableHead>
                      <TableRow
                        sx={{
                          '& th': {
                            fontWeight: 800,
                            bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.18 : 0.22),
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          },
                        }}
                      >
                        <TableCell>Rank</TableCell>
                        <TableCell>Level</TableCell>
                        <TableCell align="right">Direct refs</TableCell>
                        <TableCell align="right">Bonus</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {RANK_DIAMOND_BONUSES.map((row, idx) => {
                        const claimed = claimedRankRewardIds.includes(row.id);
                        const tierBlock = rankClaimBlockedReason(
                          row,
                          rankQualifyingLevel,
                          directReferrals.length,
                          rankProfileComplete,
                          referralsDataReady,
                          errReferrals
                        );
                        const seqBlock = rankDialogPriorSequentialBlockReason(idx, claimedRankRewardIds);
                        const blockOrSeq = tierBlock || seqBlock;
                        const canClaim = blockOrSeq === '';
                        return (
                          <TableRow
                            key={row.id}
                            sx={{ '&:last-child td': { borderBottom: 0 }, '& td': { verticalAlign: 'middle' } }}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {row.rank}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {row.levelReq}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {row.directReferrals}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 700, color: portal.pink }}>
                                {row.bonus}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              {claimed ? (
                                <Chip size="small" label="Claimed" color="success" variant="outlined" />
                              ) : canClaim ? (
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={rankClaimInFlight}
                                  onClick={() => handleClaimRankReward(row.id, row.rank, row.bonus)}
                                >
                                  {rankClaimInFlight ? '…' : 'Claim'}
                                </Button>
                              ) : (
                                <Tooltip title={blockOrSeq}>
                                  <span>
                                    <Button size="small" variant="outlined" disabled>
                                      Locked
                                    </Button>
                                  </span>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Level comes from your referral network data and profile when the server provides it. Refresh
                  after your level updates if this looks out of date.
                </Typography>
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                  Rank claims are saved to your account. Each tier can be claimed once; when you qualify for a
                  higher tier, a new Claim becomes available.
                </Alert>
              </Stack>
            )}

            {progressInfoDialogSection === 'streak' &&
              (() => {
                const best = bestStreakFromData(streakData);
                const current = streakData != null ? Math.max(0, streakData.currentStreak ?? 0) : null;
                const longest = streakData != null ? Math.max(0, streakData.longestStreak ?? 0) : null;
                const pct = Math.min(100, ((current ?? 0) / STREAK_MILESTONE_DAYS) * 100);
                return (
                  <Stack spacing={1.5}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: alpha(theme.palette.text.primary, 0.78),
                        lineHeight: 1.45,
                      }}
                    >
                      Users can win one of these rewards. {STREAK_MILESTONE_DAYS} streak and program rules apply.
                    </Typography>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: alpha(portal.cyan, theme.palette.mode === 'dark' ? 0.22 : 0.18),
                        background:
                          theme.palette.mode === 'dark'
                            ? `linear-gradient(145deg, ${alpha(portal.surfaceDeep, 0.9)} 0%, ${alpha(portal.purple, 0.07)} 100%)`
                            : alpha(portal.cyan, 0.06),
                        boxShadow: `inset 0 1px 0 ${alpha('#fff', theme.palette.mode === 'dark' ? 0.04 : 0.5)}`,
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        flexWrap="wrap"
                        gap={0.75}
                        sx={{ mb: 1 }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 800,
                            letterSpacing: 0.4,
                            textTransform: 'uppercase',
                            fontSize: '0.7rem',
                            color: alpha(theme.palette.text.primary, 0.85),
                          }}
                        >
                          Progress
                        </Typography>
                        <Typography variant="body2" sx={{ color: alpha(theme.palette.text.secondary, 0.95) }}>
                          {streakData == null && loadingStreak
                            ? 'Loading streak…'
                            : streakData == null
                              ? 'Streak unavailable — refresh to load'
                              : `${current} / ${STREAK_MILESTONE_DAYS} · Longest ${longest}`}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={streakData == null ? 0 : pct}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.08 : 0.12),
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            background: portal.gradientBold,
                            boxShadow: `0 0 20px ${alpha(portal.cyan, 0.35)}`,
                          },
                        }}
                      />
                      {best < STREAK_MILESTONE_DAYS && streakData != null && (
                        <Typography
                          variant="caption"
                          sx={{
                            mt: 0.5,
                            display: 'block',
                            color: alpha(theme.palette.text.secondary, 0.95),
                            lineHeight: 1.35,
                          }}
                        >
                          Unlocks when Current or Longest reaches {STREAK_MILESTONE_DAYS} · rules apply.
                        </Typography>
                      )}
                    </Paper>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mb: 1,
                        color: alpha(theme.palette.text.secondary, 0.95),
                        lineHeight: 1.4,
                      }}
                    >
                      Tap or hover a prize to see eligible countries.
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                        gap: 1.25,
                        alignItems: 'stretch',
                      }}
                    >
                      {STREAK_365_REWARD_ROWS.map((row) => {
                        const claimed = claimedStreakRewardIds.includes(row.id);
                        const canClaim = best >= STREAK_MILESTONE_DAYS && streakData != null;
                        const prizeChoiceAlreadyMade = claimedStreakRewardIds.length > 0;
                        const canClaimThisRow =
                          canClaim && (!prizeChoiceAlreadyMade || claimed);
                        return (
                          <Tooltip
                            key={row.id}
                            title={<Streak365EligibilityTooltip eligibilityKey={row.eligibilityKey} />}
                            arrow
                            placement="top"
                            enterTouchDelay={0}
                            leaveTouchDelay={4000}
                            slotProps={{
                              tooltip: {
                                sx: {
                                  maxWidth: 300,
                                  p: 1.25,
                                  bgcolor: theme.palette.mode === 'dark' ? '#1a1f2e' : 'grey.900',
                                  border: `1px solid ${alpha(portal.cyan, 0.25)}`,
                                  boxShadow: `0 12px 32px ${alpha('#000', 0.35)}`,
                                },
                              },
                            }}
                          >
                            <Paper
                              elevation={0}
                              sx={{
                                p: { xs: 1.25, sm: 1.5 },
                                minWidth: 0,
                                height: '100%',
                                borderRadius: 2.5,
                                cursor: 'help',
                                border: '1px solid',
                                borderColor: alpha(theme.palette.divider, 0.45),
                                bgcolor:
                                  theme.palette.mode === 'dark'
                                    ? alpha('#fff', 0.03)
                                    : alpha(theme.palette.background.paper, 0.9),
                                transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
                                '&:hover': {
                                  borderColor: alpha(portal.cyan, 0.45),
                                  boxShadow: `0 10px 32px ${alpha(portal.cyan, theme.palette.mode === 'dark' ? 0.15 : 0.12)}`,
                                  transform: 'translateY(-2px)',
                                },
                              }}
                            >
                              <Stack spacing={1.25} sx={{ height: '100%' }}>
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                  <Box
                                    sx={{
                                      width: 36,
                                      height: 36,
                                      borderRadius: 1.5,
                                      flexShrink: 0,
                                      display: 'grid',
                                      placeItems: 'center',
                                      background: alpha(portal.cyan, theme.palette.mode === 'dark' ? 0.12 : 0.1),
                                      border: `1px solid ${alpha(portal.cyan, 0.22)}`,
                                    }}
                                  >
                                    <PublicIcon sx={{ fontSize: 18, color: portal.cyan }} />
                                  </Box>
                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: 800,
                                        letterSpacing: 0.15,
                                        color: theme.palette.text.primary,
                                        lineHeight: 1.3,
                                        fontSize: { xs: '0.875rem', sm: '0.95rem' },
                                        wordBreak: 'break-word',
                                      }}
                                    >
                                      {streakRewardDisplayLabel(row)}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: 'block',
                                        mt: 0.35,
                                        color: alpha(theme.palette.text.secondary, 0.9),
                                        fontSize: '0.68rem',
                                      }}
                                    >
                                      View eligibility
                                    </Typography>
                                  </Box>
                                </Stack>
                                {(claimed || canClaimThisRow) && (
                                  <Box sx={{ mt: 'auto', pt: 0.25 }}>
                                    {claimed ? (
                                      <Chip
                                        size="small"
                                        label="Claimed"
                                        color="success"
                                        variant="outlined"
                                        sx={{ fontWeight: 700 }}
                                      />
                                    ) : (
                                      <Button
                                        size="small"
                                        variant="contained"
                                        fullWidth
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleClaimStreakReward(row.id, row.reward);
                                        }}
                                        sx={{
                                          borderRadius: 1.5,
                                          background: portal.gradientBold,
                                          color: '#fff',
                                          fontWeight: 800,
                                          py: 0.65,
                                          fontSize: '0.75rem',
                                          boxShadow: `0 4px 16px ${alpha(portal.indigo, 0.35)}`,
                                          '&:hover': {
                                            background: portal.gradientHeroHover,
                                          },
                                        }}
                                      >
                                        Claim
                                      </Button>
                                    )}
                                  </Box>
                                )}
                              </Stack>
                            </Paper>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </Stack>
                );
              })()}

            {progressInfoDialogSection === 'downline' && (
              <Stack spacing={1.25}>
                <Typography
                  variant="body2"
                  sx={{
                    color: alpha(theme.palette.text.primary, 0.78),
                    lineHeight: 1.45,
                  }}
                >
                  You earn <strong>Dreamey Diamonds</strong> when each <strong>direct referral</strong> successfully
                  reaches the streak milestones below. Rewards apply <strong>per member</strong> (each milestone
                  can be claimed once per referral, per cycle). You need at least{' '}
                  <strong>{MIN_DOWNLINE_FOR_DREAMEY_STREAK} direct referrals</strong> to participate;{' '}
                  <strong>all</strong> of your direct referrals can earn these milestones for you.
                  Eligible countries: all (unless your program rules say otherwise).
                </Typography>
                {referralsDataReady && directReferrals.length < MIN_DOWNLINE_FOR_DREAMEY_STREAK && (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    You have {directReferrals.length} direct referral
                    {directReferrals.length !== 1 ? 's' : ''}. Add at least{' '}
                    {MIN_DOWNLINE_FOR_DREAMEY_STREAK - directReferrals.length} more to unlock Dreamey Diamond streak
                    rewards.
                  </Alert>
                )}
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 0.25,
                    fontWeight: 800,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    color: alpha(theme.palette.text.secondary, 0.9),
                  }}
                >
                  Your referrals
                </Typography>
                {!isViewingOwnActivity && (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Downline streak rewards can only be claimed when you are viewing your own activity (not another
                    member&apos;s profile).
                  </Alert>
                )}
                {loadingReferrals && (
                  <Typography variant="body2" color="text.secondary">
                    Loading your referrals…
                  </Typography>
                )}
                {!loadingReferrals && errReferrals && (
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    Could not load referrals: {errReferrals}. Use Refresh on Activity.
                  </Alert>
                )}
                {isViewingOwnActivity && loadingDownlineStreak && (
                  <Typography variant="body2" color="text.secondary">
                    Loading claim status…
                  </Typography>
                )}
                {isViewingOwnActivity && errDownlineStreak && (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    {errDownlineStreak}
                  </Alert>
                )}
                {referralsDataReady && directReferrals.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    You have no direct referrals yet. Invite others to unlock downline streak rewards.
                  </Typography>
                )}
                {referralsDataReady && directReferrals.length > 0 && (
                  <Stack spacing={0.5} sx={{ width: '100%' }}>
                    {downlineReferralsByStreakDesc.map((ref) => {
                    const progressMatch = (referralsProgress?.referrals || []).find(
                      (r) => Number(r.userId) === Number(ref.UserID)
                    );
                    const best = bestReferralStreakFromProgress(progressMatch);
                    const current = referralCurrentStreakFromProgress(progressMatch);
                    const cycleRow = getDownlineCycleRowForRef(downlineCycleState, ref.UserID);
                    const rewardCycle = Number(cycleRow.rewardCycle ?? 1);
                    const baseline = Number(cycleRow.baselineStreak ?? 0);
                    const effective = Math.max(0, current - baseline);
                    const displayName =
                      [ref.FirstName, ref.LastName].filter(Boolean).join(' ') ||
                      ref.UserName ||
                      `User #${ref.UserID}`;
                    const programOk =
                      referralsDataReady &&
                      directReferrals.length >= MIN_DOWNLINE_FOR_DREAMEY_STREAK;

                    return (
                      <Paper
                        key={ref.UserID || ref.ReferralID}
                        elevation={0}
                        sx={{
                          position: 'relative',
                          overflow: 'hidden',
                          p: { xs: 1, sm: 1.15 },
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: alpha(portal.purple, 0.32),
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? alpha(portal.purple, 0.07)
                              : alpha(portal.purple, 0.04),
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 3,
                            borderRadius: '3px 0 0 3px',
                            background: portal.gradientBold,
                          },
                        }}
                      >
                        <Stack spacing={0.75} sx={{ pl: 0.35 }}>
                          <Stack
                            direction="row"
                            alignItems="flex-start"
                            justifyContent="space-between"
                            flexWrap="wrap"
                            gap={0.75}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 800, letterSpacing: 0.2, fontSize: { xs: '0.95rem', sm: '1rem' } }}
                            >
                              {displayName}
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap>
                              <Chip
                                size="small"
                                label={`${current} 🔥`}
                                sx={{
                                  height: 22,
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  bgcolor: alpha(portal.amber, 0.14),
                                  border: `1px solid ${alpha(portal.amber, 0.4)}`,
                                  color: theme.palette.mode === 'dark' ? portal.amber : theme.palette.warning.dark,
                                }}
                              />
                              <Chip
                                size="small"
                                label={`Best: ${best} d`}
                                sx={{
                                  height: 22,
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  bgcolor: alpha(portal.cyan, 0.12),
                                  border: `1px solid ${alpha(portal.cyan, 0.25)}`,
                                }}
                              />
                            </Stack>
                          </Stack>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: {
                                xs: 'repeat(2, minmax(0, 1fr))',
                                sm: 'repeat(3, minmax(0, 1fr))',
                              },
                              gap: 0.5,
                            }}
                          >
                            {DOWNLINE_STREAK_DREAMEY_MILESTONES.map((m) => {
                              const ck = downlineDreameyClaimKey(ref.UserID, m.days, rewardCycle);
                              const claimed = claimedDownlineStreakKeys.includes(ck);
                              const prevOk = isPrevDownlineMilestoneClaimed(
                                claimedDownlineStreakKeys,
                                ref.UserID,
                                m.days,
                                rewardCycle
                              );
                              const reached = effective >= m.days && programOk && prevOk;
                              let lockReason = '';
                              if (!isViewingOwnActivity) {
                                lockReason = 'View your own activity to claim';
                              } else if (!programOk) {
                                lockReason =
                                  directReferrals.length < MIN_DOWNLINE_FOR_DREAMEY_STREAK
                                    ? `Need ${MIN_DOWNLINE_FOR_DREAMEY_STREAK}+ direct referrals`
                                    : 'Not eligible';
                              } else if (!prevOk) {
                                lockReason = 'Claim the previous milestone in this cycle first';
                              } else if (effective < m.days) {
                                lockReason = `Need ${m.days}-day effective streak this cycle (now ${effective} d)`;
                              }

                              return (
                                <Box key={ck} sx={{ minWidth: 0 }}>
                                  {claimed ? (
                                    <Chip
                                      size="small"
                                      label={`${formatDownlineMilestoneLabel(m)} ✓`}
                                      color="success"
                                      variant="outlined"
                                      sx={{
                                        width: '100%',
                                        height: 26,
                                        fontSize: '0.68rem',
                                        fontWeight: 700,
                                        justifyContent: 'center',
                                      }}
                                    />
                                  ) : reached && isViewingOwnActivity ? (
                                    <Button
                                      fullWidth
                                      size="small"
                                      variant="contained"
                                      disabled={downlineClaimInFlight}
                                      onClick={() =>
                                        handleClaimDownlineDreamey(
                                          ref.UserID,
                                          m.days,
                                          m.coins,
                                          displayName,
                                          rewardCycle
                                        )
                                      }
                                      sx={{
                                        borderRadius: 1.5,
                                        fontWeight: 800,
                                        py: 0.45,
                                        minHeight: 26,
                                        fontSize: '0.68rem',
                                        background: portal.gradientBold,
                                        color: '#fff',
                                        boxShadow: `0 4px 14px ${alpha(portal.indigo, 0.3)}`,
                                        '&:hover': { background: portal.gradientHeroHover },
                                      }}
                                    >
                                      {formatDownlineMilestoneLabel(m)}
                                    </Button>
                                  ) : (
                                    <Tooltip title={lockReason}>
                                      <span style={{ display: 'block', width: '100%' }}>
                                        <Button
                                          fullWidth
                                          size="small"
                                          variant="outlined"
                                          disabled
                                          sx={{
                                            minWidth: 0,
                                            borderRadius: 1.5,
                                            py: 0.45,
                                            minHeight: 26,
                                            fontSize: '0.68rem',
                                            fontWeight: 700,
                                            borderColor: alpha(theme.palette.text.primary, 0.15),
                                            color: alpha(theme.palette.text.primary, 0.4),
                                          }}
                                        >
                                          {formatDownlineMilestoneLabel(m)}
                                        </Button>
                                      </span>
                                    </Tooltip>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        </Stack>
                      </Paper>
                    );
                  })}
                  </Stack>
                )}
                <Alert
                  severity="info"
                  variant="outlined"
                  sx={{ borderRadius: 2, py: 0.75, '& .MuiAlert-message': { py: 0 } }}
                >
                  Milestones must be claimed in order within each reward cycle. After a streak breaks or you complete
                  the 365-day tier, the cycle advances; unclaimed milestones in that cycle are forfeited. Claims are
                  stored on your account.
                </Alert>
              </Stack>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              px: 2.5,
              py: 2,
              borderTop: '1px solid',
              borderColor: alpha(theme.palette.divider, 0.45),
              bgcolor: alpha(portal.surfaceDeep, theme.palette.mode === 'dark' ? 0.5 : 0),
              justifyContent: 'flex-end',
            }}
          >
            <Button
              onClick={closeStreakRewardsDialog}
              variant="contained"
              sx={{
                px: 3,
                py: 1,
                fontWeight: 800,
                color: '#fff',
                background: portal.gradientBold,
                boxShadow: `0 6px 20px ${alpha(portal.indigo, 0.35)}`,
                '&:hover': {
                  background: portal.gradientHeroHover,
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={claimSnackbar.open}
          autoHideDuration={6500}
          onClose={() => setClaimSnackbar((s) => ({ ...s, open: false }))}
          message={claimSnackbar.message}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
    </Box>
  );
}
